/**
 * Home Tab Component
 *
 * Displays the main roster view including:
 * - Collapsible generate form
 * - History trail of roster versions
 * - Counter timetable grid
 * - Officer schedule grid
 * - Dev-mode debug matrices panel
 * - Operation messages inbox modal
 */

import { useOfficerOrder } from "@/utils/officerOrder";
import { useVersions } from "@/utils/versionStore";
import { gridTraces } from "@/utils/stateSync";
import { useState, useMemo, useEffect, useCallback, useRef, useLayoutEffect, lazy, Suspense } from "react";
import { downloadEditLog } from "@/utils/exportEditLog";
import { createPortal } from "react-dom";
// The wizard (and the PDF preview + pdf.js it pulls in) is a large share of the
// app's code but only needed once a roster is being (re)generated — load it as
// its own chunk so it isn't on the critical path of every page load / session
// resume. It is prefetched once the page is idle (below), so opening it is
// still instant.
const loadGenerateWizard = () => import("@/components/GenerateWizard");
const GenerateWizard = lazy(() => loadGenerateWizard().then((m) => ({ default: m.GenerateWizard })));
import { RosterGrid } from "@/components/RosterGrid";
import { HotoMessageModal } from "@/components/HotoMessageModal";
import { setHotoNote, useHotoNotes } from "@/utils/hotoNotes";
import { PinDialog } from "@/components/EditModeToggle";
import type { UseRosterStateReturn } from "@/hooks/useRosterState";
import { OfficerScheduleGrid } from "@/components/OfficerScheduleGrid";
import { NoReliefProvider, type NoReliefView } from "@/contexts/NoReliefContext";
import { markAction, noReliefEnds, noReliefMarks } from "@/utils/noRelief";
import { OfficerEditPopup } from "@/components/OfficerEditPopup";
import { ViewOnlyBanner } from "@/components/ViewOnlyBanner";
import { SharedAutoAssignModal } from "@/components/SharedAutoAssignModal";
import { officersWithUnassignedTime, readBreakOverrides } from "@/utils/unassignedOfficers";
import { AddOfficersDrawer } from "@/components/AddOfficersDrawer";
import { UI_SCALE } from "@/utils/uiScale";
import { CompareVersionsModal } from "@/components/CompareVersionsModal";
import { GridDensitySelect } from "@/components/GridDensitySelect";
import { NotesModal } from "@/components/NotesModal";
import { NoReliefModal } from "@/components/NoReliefModal";
import { NoReliefReminder } from "@/components/NoReliefReminder";
import { useNoRelief } from "@/hooks/useNoRelief";
import { useCallAcks } from "@/hooks/useCallAcks";
import type { CallBlock } from "@/utils/callBlocks";
import { useSession } from "@/contexts/SessionContext";
import { LiveStatsText } from "@/components/LiveStatsText";
import { StatsFooterLines } from "@/components/StatsFooterLines";
import { StatsFontControls } from "@/components/StatsFontControls";
import { useOtColorVersion } from "@/utils/otColors";
import { ContactsModal } from "@/components/ContactsModal";
import { modeColors } from "@/utils/modeColors";
import { StatsFilterPopover } from "@/components/StatsFilterPopover";
import { WaIcon } from "@/components/WhatsAppShared";
import { Button } from "@/components/Button";
import { Copyable } from "@/components/Copyable";
import { apiClient, VIEW_ONLY_MESSAGE } from "@/api/client";
import type {
  RosterState,
  OperationMessage,
  DebugMatrices,
  HoverInfo,
  OperationResult,
} from "@/types/api";
import {
  hmmmToSlot,
  hmmmToEndSlot,
  getStartHour,
  getModeConfig,
  formatCounterLabel,
} from "@/config/modeConfig";
import type { AssignmentRow } from "@/components/OfficerCard";
import { useNotification } from "@/contexts/NotificationContext";
import { usernameColor } from "@/utils/userColor";
import {
  loadWizardDraft,
  setWizardOpen as persistWizardOpen,
  wasWizardOpen,
} from "@/utils/wizardDraft";

import { isTouchDevice, useViewportTier } from "@/components/tabs/home/useViewportTier";
import {
  buildDutyRows,
  buildGridData,
  deriveOfficerScheduleWindows,
  messageHasCaution,
  patchGridDataForPreview,
} from "@/components/tabs/home/gridData";
import { dutyRowLabels, useDutyOptions } from "@/utils/dutyOptions";
import { useNoReliefAlert } from "@/utils/noReliefAlert";
import { printedBreakSlots } from "@/utils/printedBreaks";
import {
  HidePaneHint,
  HistoryIconButton,
  IconTabButton,
  InboxTabButton,
  NoReliefTabButton,
  SplitControlButton,
} from "@/components/tabs/home/HomeTabButtons";
import { useSplitPane } from "@/components/tabs/home/useSplitPane";
import { usePdfExport } from "@/components/tabs/home/usePdfExport";
import { useStatsPanel } from "@/components/tabs/home/useStatsPanel";
import { OfficerSearch } from "@/components/OfficerSearch";
import { ExportPreviewModal } from "@/components/tabs/home/ExportPreviewModal";
import { InboxModal } from "@/components/tabs/home/InboxModal";

interface HomeTabProps {
  activeView: 'counter' | 'officers';
  /** False while another tab (Settings…) is showing and this stays mounted but hidden. Defaults to true. */
  visible?: boolean;
  state: RosterState | null;
  refetch: () => void;
  /** Reconciles the non-grid fields (history, metadata, counts) after an edit whose
   *  response already patched the grid — from this tab's own WebSocket echo, with a
   *  refetch only as a fallback. Cheaper than refetch() for every edit. */
  reconcileAfterEdit: () => void;
  /** Applies a full state snapshot locally without a network round trip — used by
   * undo/redo/restore, which already get the post-restore state back from the API. */
  applySnapshot: (snapshot: RosterState) => void;
  /** Instantly patches the grid from a mutation's own response (no extra round trip);
   * reconcileAfterEdit() should still follow to reconcile non-grid fields. */
  patchFromOperation: (result: Pick<OperationResult, 'snapshot'> | undefined) => void;
  /** Instantly jumps the grid to an already-cached history entry — used for optimistic
   * undo/redo so the graphic updates before the round trip completes. */
  patchHistoryPointer: (next: number | ((current: number) => number)) => void;
  /** Strips an officer out of the grid before removeOfficer's round trip resolves.
   * Returns a rollback to call if that call fails. */
  optimisticRemoveOfficer: (officerKeys: string | string[]) => () => void;
  /** Same, for "clear all counters" — keeps the officer's row, empties their schedule.
   *  Accepts one officer key or several for a bulk clear in a single state update. */
  optimisticClearOfficerCounters: (officerKeys: string | string[]) => () => void;
  /** Same, but for clearing only a chosen set of [startSlot, endSlot) ranges — lets
   *  the "Clear Counters" picker clear specific assignment windows, not just whole
   *  officers. */
  optimisticClearOfficerRanges: (ranges: Array<{ officerKey: string; startSlot: number; endSlot: number }>) => () => void;
  /** Moves an officer's block on the grid before the save returns — see useRosterState. */
  optimisticMoveBlock: UseRosterStateReturn['optimisticMoveBlock'];
  sessionId: string;
  isDevUser: boolean;
  operationMessages: OperationMessage[];
  onClearMessages: () => void;
  onSwitchTab: (tab: string) => void;
  onOperationMessage?: (msg: OperationMessage) => void;
  /** Highlighted cells from another user's recent edit — fades out after 5s. */
  editTraces?: Array<{ row: number; col: number; color: string; traceType?: 'add' | 'delete' | 'change' }>;
  /** Incremented when another user changes the roster — clears local redo state. */
  externalChangeTick?: number;
  /** Cells other users currently have open for editing (live, Google-Docs-style presence). */
  cellPresence?: Array<{ row: number; col: number; officerKey?: string; userName: string }>;
  /** Broadcasts that this user opened/closed a cell for editing — no-op if omitted. */
  onCellFocus?: (row: number, col: number, officerKey?: string) => void;
  onCellBlur?: () => void;
  /** DOM nodes (rendered by the parent tab bar) that the Inbox button portals into,
   * so it sits inline with the Counter/Officers/Settings tabs instead of its own row. */
  inboxAnchorDesktop?: HTMLDivElement | null;
  inboxAnchorMobile?: HTMLDivElement | null;
  /** Same portal pattern as the Inbox anchors, for the Notes and Contacts buttons that
   * sit alongside it in the tab bar / bottom nav. */
  notesAnchorDesktop?: HTMLDivElement | null;
  notesAnchorMobile?: HTMLDivElement | null;
  contactsAnchorDesktop?: HTMLDivElement | null;
  contactsAnchorMobile?: HTMLDivElement | null;
  /** Same portal pattern again, for the No Relief button. */
  noReliefAnchorDesktop?: HTMLDivElement | null;
  noReliefAnchorMobile?: HTMLDivElement | null;
  /** Bumped by App on every "no_relief_update" WS message, so the call log
   * re-fetches when another user ticks a counter. */
  noReliefUpdateTick?: number;
  /** Bumped by App on every "call_ack_update" WS message, so the officer phone
   * reminders re-fetch when another user marks a call. */
  callAckUpdateTick?: number;
  /** Called after Inbox/Notes/Contacts is picked from the mobile "More" popover —
   * a portaled click doesn't bubble through the popover's own DOM container (React
   * bubbles portals along the component tree, not the DOM tree), so App can't just
   * close the popover with an onClick at that container; it needs this instead. */
  onMobileMenuAction?: () => void;
  /** Bumped by App on every "notes_update" WS message — passed straight through
   * so an open Notes panel can re-fetch when another user locks/edits/deletes. */
  notesUpdateTick?: number;
  /** DOM node the Split/Stack view control portals into — left of Inbox in the desktop
   * tab row, replacing the separate copies that used to live in each pane's own header. */
  splitControlAnchor?: HTMLDivElement | null;
}

/** Total height (36px row + 1px top rule) of the Counter tab's docked Ctr Stats
 *  bar — the split Officers pane's Add Officers button matches it exactly. */
const SPLIT_FOOTER_H = 37;

export function HomeTab({
  activeView,
  visible = true,
  state,
  refetch,
  reconcileAfterEdit,
  applySnapshot,
  patchFromOperation,
  patchHistoryPointer,
  optimisticRemoveOfficer,
  optimisticClearOfficerCounters,
  optimisticClearOfficerRanges,
  optimisticMoveBlock,
  sessionId: _sessionId,
  isDevUser,
  operationMessages,
  onClearMessages,
  onSwitchTab: _onSwitchTab,
  onOperationMessage,
  editTraces,
  cellPresence,
  onCellFocus,
  onCellBlur,
  inboxAnchorDesktop,
  inboxAnchorMobile,
  notesAnchorDesktop,
  notesAnchorMobile,
  contactsAnchorDesktop,
  contactsAnchorMobile,
  noReliefAnchorDesktop,
  noReliefAnchorMobile,
  noReliefUpdateTick = 0,
  callAckUpdateTick = 0,
  onMobileMenuAction,
  notesUpdateTick,
  splitControlAnchor,
}: HomeTabProps) {
  useOfficerOrder();   // officer lists below re-sort when the user drags rows
  const { dismissSpinner, notifyWarning, notifyError, notifyInfo, notifySuccess, notify } = useNotification();
  // Opened straight back up after a refresh when the user was mid-setup: having
  // chosen to resume, they should not have to choose again on every reload.
  const [wizardOpen, setWizardOpenState] = useState(
    () => !!_sessionId && wasWizardOpen(_sessionId) && !!loadWizardDraft(_sessionId),
  );
  const [showStatsDisplay, setShowStatsDisplay] = useState(false);
  const { canEdit, accessMode, setCanEdit, sessionId, userName: myUserName } = useSession();
  // This user's choice for this session: the reminder popup, the badge only, or neither.
  const noReliefAlert = useNoReliefAlert(sessionId);
  // Generating writes a roster, so a view-only user is asked for the edit PIN right
  // here — rather than bounced to the header slider — and carried into the wizard.
  const [pinForWizard, setPinForWizard] = useState(false);
  const openWizard = (open: boolean) => {
    // No use walking a view-only user through the whole wizard only to refuse
    // them at the end. A session joined by ID can unlock on the spot; one opened
    // from past sessions stays view-only.
    if (open && !canEdit) {
      if (accessMode === 'id' && _sessionId) setPinForWizard(true);
      else notifyWarning(VIEW_ONLY_MESSAGE);
      return;
    }
    setWizardOpenState(open);
    if (_sessionId) persistWizardOpen(_sessionId, open);
  };
  // The wizard comes back open after a refresh or a new tab, but edit rights belong to a tab: one that lost them
  // is asked for the PIN right there, with the wizard (and its draft) waiting behind it — not dropped into a
  // wizard that refuses every write, from which the only way out used to be closing it and losing the draft.
  useEffect(() => {
    if (!wizardOpen || canEdit) return;
    if (accessMode === 'id' && _sessionId) {
      setPinForWizard(true);
    } else {
      setWizardOpenState(false);
      if (_sessionId) persistWizardOpen(_sessionId, false);   // the draft stays; only the open flag goes
      notifyWarning(VIEW_ONLY_MESSAGE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardOpen, canEdit, accessMode, _sessionId]);
  // Which step an unfinished pass through the wizard reached, if there is one.
  // Re-read whenever the wizard closes, since that is when it is written or cleared.
  const [resumableStep, setResumableStep] = useState<number | null>(null);
  useEffect(() => {
    if (wizardOpen) return;
    const draft = _sessionId ? loadWizardDraft(_sessionId) : null;
    setResumableStep(draft ? Math.min(Math.max(draft.step, 2), 4) : null);
  }, [wizardOpen, _sessionId]);
  // Persisted across counter/officers tab switches so the edit popup stays open
  const [editedOfficer, setEditedOfficer] = useState<HoverInfo | null>(null);

  // Broadcast live cell presence: fires whenever the officer edit popup opens/closes,
  // i.e. whenever a user clicks a cell — mirrors Google-Docs-style "someone's here now".
  // row/col === -1 is the sentinel the multi-officer picker uses for an ambiguous cell; skip it.
  // wasFocusedRef tracks whether *we* actually sent a focus, so blur only fires on a real
  // transition (not on initial mount, when editedOfficer starts null) and also fires once
  // on unmount if a cell was left open (HomeTab is normally kept mounted across tab
  // switches, but this guards the case where it isn't).
  const wasFocusedRef = useRef(false);
  useEffect(() => {
    if (editedOfficer && editedOfficer.row >= 0 && editedOfficer.col >= 0) {
      onCellFocus?.(editedOfficer.row, editedOfficer.col, editedOfficer.officer);
      wasFocusedRef.current = true;
    } else if (wasFocusedRef.current) {
      onCellBlur?.();
      wasFocusedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedOfficer?.row, editedOfficer?.col, editedOfficer?.officer]);

  useEffect(() => {
    return () => { if (wasFocusedRef.current) onCellBlur?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Incremented to signal OfficerScheduleGrid to close all its internal popups
  const [officerGridCloseSignal, setOfficerGridCloseSignal] = useState(0);
  // Shared auto-assign modal — jointly fills unassigned slots across multiple officers
  const [showSharedAutoAssign, setShowSharedAutoAssign] = useState(false);
  // End-of-shift HOTO message: the popup, and the notes typed beside OT officers' rows
  // (officer key -> text), which belong to the session so every device sees them.
  const [showHoto, setShowHoto] = useState(false);
  const hotoNotes = useHotoNotes();
  // Multi-card panel: pinned officer cards + expand state
  const [pinnedOfficerIds, setPinnedOfficerIds] = useState<string[]>([]);
  const [pinnedClickedTimes, setPinnedClickedTimes] = useState<Record<string, string>>({});
  // The officer sheet opens at its normal (2/3) size; the expand button takes it full-screen.
  const [panelExpanded, setPanelExpanded] = useState(false);
  // PDF state persisted across wizard open/close so re-opening shows the last uploaded file
  const [cachedPdf, setCachedPdf] = useState<{
    file: File;
    detectedShift: "morning" | "night" | null;
    detectedMode: "arrival" | "departure" | null;
  } | null>(null);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState<number | null>(null);
  // Undo/redo availability is derived from the server's version pointer (state.history_index),
  // so it is inherently correct across collaborators — no client-side redo bookkeeping.
  const historyIdx = state ? (state.history_index ?? state.history.length - 1) : -1;
  const canUndo = !!state && historyIdx > 0;
  const canRedo = !!state && historyIdx >= 0 && historyIdx < state.history.length - 1;
  const canCompare = !!state && state.history.length >= 2;
  const [showInbox, setShowInbox] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  // "Show me this counter" requests from the officer card's counter dropdown; the
  // nonce lets re-picking the same counter scroll back to it after the user wandered off.
  const [revealRequest, setRevealRequest] = useState<{ row: number; startSlot: number | null; nonce: number } | null>(null);
  // Count of notes with any text — a quiet "this board isn't empty" cue on the
  // Notes button. Own edits don't tick notesUpdateTick, so closing the panel
  // refetches too.
  const [filledNotesCount, setFilledNotesCount] = useState(0);
  useEffect(() => {
    if (showNotes || !state?.generated) return;
    let cancelled = false;
    apiClient.getNotes()
      .then((notes) => { if (!cancelled) setFilledNotesCount(notes.filter((n) => n.text.trim() !== '').length); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showNotes, notesUpdateTick, state?.generated]);
  const [showContacts, setShowContacts] = useState(false);
  const [showNoRelief, setShowNoRelief] = useState(false);
  // Always the live roster, never a history version being previewed: calls are
  // made about what is actually on the floor now.
  const noRelief = useNoRelief(
    state?.generated ? state.current_snapshot?.counter_matrix : undefined,
    _sessionId,
    noReliefUpdateTick,
  );
  // Officer phone reminders: also the live roster, never a version being previewed.
  const callAcks = useCallAcks(
    state?.generated ? state.current_snapshot?.counter_matrix : undefined,
    state?.officer_metadata,
    state?.initial_officer_schedules ?? undefined,
    _sessionId,
    callAckUpdateTick,
  );
  const setCalled = callAcks.setCalled;
  const toggleBlockCall = useCallback((block: CallBlock, called: boolean) => {
    const who = state?.officer_metadata?.[block.officerId]?.name ?? 'Officer';
    setCalled(block.officerId, [block.signature], called)
      .then(() => (called
        ? notifySuccess(`${who} marked as informed — the phone icon turns grey`)
        : notifyInfo(`${who} marked as NOT informed — the phone icon is active again`)))
      .catch(() => notifyWarning('Could not save the call — try again.'));
  }, [setCalled, notifyWarning, notifySuccess, notifyInfo, state?.officer_metadata]);
  const callBlocks = callAcks.blocks;
  const callByOfficer = callAcks.byOfficer;
  const toggleOfficerCall = useCallback((officerId: string, called: boolean) => {
    const sigs = called
      ? (callByOfficer.get(officerId)?.pending ?? []).map((b) => b.signature)
      : callBlocks.filter((b) => b.officerId === officerId).map((b) => b.signature);
    if (sigs.length === 0) return;
    setCalled(officerId, sigs, called)
      .catch(() => notifyWarning('Could not save the call — try again.'));
  }, [setCalled, callByOfficer, callBlocks, notifyWarning]);
  // Keys of cautioned messages the user has opened and closed — everything else with a
  // caution flag is unread by default; messages with no flags are read on arrival.
  const [readMsgKeys, setReadMsgKeys] = useState<Set<string>>(new Set());
  const [expandedMsgKeys, setExpandedMsgKeys] = useState<Set<string>>(new Set());
  // Rendered height of the docked bar (with its expanded panel) so the Counter pane
  // can leave that much room below its rows instead of the bar covering them.
  const [dockBarEl, setDockBarEl] = useState<HTMLDivElement | null>(null);
  const [dockBarH, setDockBarH] = useState(0);
  useEffect(() => {
    if (!dockBarEl) { setDockBarH(0); return; }
    const measure = () => setDockBarH(dockBarEl.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(dockBarEl);
    return () => ro.disconnect();
  }, [dockBarEl]);
  // The page's outermost background (the frame around the panes) takes the mode's
  // colour — green for ARR, orange for DEP, matching the pill — via a CSS variable
  // that App's wrapper and <body> read. Cleared when leaving the roster.
  const pageTint = state?.generated && state.operation_mode ? modeColors(state.operation_mode).tint : null;
  useEffect(() => {
    if (!pageTint) return;
    document.documentElement.style.setProperty('--mode-bg', pageTint);
    return () => { document.documentElement.style.removeProperty('--mode-bg'); };
  }, [pageTint]);

  // Drawer state for adding SOS/OT officers inline without leaving the Home tab
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSubTab, setDrawerSubTab] = useState<'main' | 'sos' | 'ot'>('sos');
  // Touch tablets (iPad) default to a bottom sheet like phone, but — unlike phone —
  // have room to offer a right-panel choice instead. Desktop (non-touch) always gets
  // the right panel; phone always gets the bottom sheet; neither shows this toggle.
  const [drawerPlacementPref, setDrawerPlacementPref] = useState<'bottom' | 'right'>('bottom');
  // Measures the open drawer's rendered height so the docked Manning Stats bar
  // (below) can pin itself right above it instead of ending up hidden underneath.
  const drawerPanelRef = useRef<HTMLDivElement>(null);
  const [drawerHeight, setDrawerHeight] = useState(0);
  // Same idea, but width — a right-side drawer in side-by-side split used to just
  // sit fixed on top of the Counter/Officer panes, clipping whichever one it
  // covered rather than making room for it. The panes now donate this much width
  // back (see splitContentRightPad below) so both stay fully visible, shrunk.
  const [drawerWidth, setDrawerWidth] = useState(0);
  // Distance from the viewport bottom to the split-divider's own bottom edge, in
  // Stack view (Counter above Officer) only — the docked Manning Stats bar pins
  // itself here instead of at the drawer's own edge, so it never lands in the
  // gap below the divider where it used to sit under (rather than above) it.
  const [dividerBottomOffset, setDividerBottomOffset] = useState(0);

  // "Add Officers" button visibility tracking — when scrolled off-screen, a fixed overlay appears
  const [addBtnEl, setAddBtnEl] = useState<HTMLButtonElement | null>(null);
  const [addBtnInView, setAddBtnInView] = useState(true);
  // Officers pane left edge — used to position the fixed overlay in horizontal split mode
  const officersPaneRef = useRef<HTMLDivElement>(null);
  const [officersPaneLeft, setOfficersPaneLeft] = useState(0);
  // Top offset of split container from viewport — used to pin sidebar below header+nav
  const [splitContainerTop, setSplitContainerTop] = useState(0);
  // Inline panel (split mode) — ref for height measurement, height for overlay positioning
  const inlinePanelRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const [inlinePanelHeight, setInlinePanelHeight] = useState(0);

  // Officer row filter — which officer IDs to hide from the grid
  const [hiddenOfficerIds, setHiddenOfficerIds] = useState<Set<string>>(new Set());
  // Live availability override — set by OfficerEditPopup for instant bracket preview on canvas
  const [liveAvailability, setLiveAvailability] = useState<{ officerId: string; segs: Array<{ start: string; end: string }> } | null>(null);
  // Live assignment preview — set by OfficerEditPopup for instant counter-grid feedback before
  // save. `vacantNotes` carries notes on vacant (no-counter) ranges, which aren't AssignmentRows.
  const [liveAssignmentPreview, setLiveAssignmentPreview] = useState<{
    officerId: string; assignments: AssignmentRow[];
    vacantNotes?: Array<{ start: string; end: string; note: string }>;
  } | null>(null);
  // Mount point in the multi-card panel's own title bar for OfficerEditPopup to portal
  // its identity + actions row into (type pill, id, WhatsApp/copy/pin/trash) — replacing
  // the generic "Officer Schedule" label and sharing a row with maximize/close — when
  // exactly one card is showing. See the panel header below and OfficerEditPopup's
  // headerAnchor prop.
  const [headerAnchorEl, setHeaderAnchorEl] = useState<HTMLDivElement | null>(null);

  // History panel — shown via 🕐 button in the pane header
  const [showHistory, setShowHistory] = useState(false);
  // Compare versions modal
  const [showCompare, setShowCompare] = useState(false);
  // Cell-level diff between the pair selected in Compare Versions — pushed onto
  // the grid live so the highlight shows there too, not just the modal's list.
  const [compareTraces, setCompareTraces] = useState<Array<{ row: number; col: number; color: string; traceType: 'add' | 'delete' | 'change' }> | null>(null);
  // Overflow menu (⋯) — Export + Regenerate actions
  const [showMenu, setShowMenu] = useState(false);
  const [officerSearch, setOfficerSearch] = useState('');

  // Touch handler is declared AFTER isSplit so it can include it in deps — see below

  const matchedOfficerIds = useMemo<Set<string> | undefined>(() => {
    const q = officerSearch.trim().toLowerCase();
    if (!q || !state?.officer_metadata) return undefined;
    const matched = new Set<string>();
    for (const [id, meta] of Object.entries(state.officer_metadata)) {
      if (
        id.toLowerCase().includes(q) ||
        (meta.name && meta.name.toLowerCase().includes(q)) ||
        (meta.deployment && meta.deployment.toLowerCase().includes(q))
      ) {
        matched.add(id);
      }
    }
    return matched.size > 0 ? matched : new Set<string>(); // empty set dims all when query has no matches
  }, [officerSearch, state?.officer_metadata]);

  // When the edit popup is open and no search is active, dim all other officers
  // the same way search does. Search takes priority when both are active.
  const effectiveHighlightIds = useMemo<Set<string> | undefined>(() => {
    if (officerSearch.trim()) return matchedOfficerIds;
    if (editedOfficer?.officer) return new Set([editedOfficer.officer]);
    return undefined;
  }, [officerSearch, matchedOfficerIds, editedOfficer?.officer]);

  // Multi-card panel: list of officers shown (pinned + active if not pinned)
  const activeOfficerId = editedOfficer?.officer;
  const panelOfficerIds = useMemo(() => {
    const ids = [...pinnedOfficerIds];
    if (activeOfficerId && !pinnedOfficerIds.includes(activeOfficerId)) {
      ids.push(activeOfficerId);
    }
    return ids;
  }, [pinnedOfficerIds, activeOfficerId]);

  // The popup's live previews only mean something while its card is on screen. A
  // gesture that dismisses the card without its close button (a press-and-hold block
  // drag calls setEditedOfficer(null)) leaves them behind, and a stale preview keeps
  // painting that officer's OLD counters over the grid — a confirmed move then
  // appeared to snap straight back.
  useEffect(() => {
    setLiveAssignmentPreview((p) => (p && !panelOfficerIds.includes(p.officerId) ? null : p));
    setLiveAvailability((a) => (a && !panelOfficerIds.includes(a.officerId) ? null : a));
  }, [panelOfficerIds]);

  // A block move rewrites the officer's counters; an open card's preview (built from
  // the pre-move assignments) would paint over it, so drop it with the move.
  const moveBlockOptimistically = useCallback((move: Parameters<typeof optimisticMoveBlock>[0]) => {
    setLiveAssignmentPreview((p) => (p && p.officerId === move.officerKey ? null : p));
    return optimisticMoveBlock(move);
  }, [optimisticMoveBlock]);

  const tier = useViewportTier();
  const {
    isSplit, splitDirection, setSplitDirection, splitRatio, setSplitEnabled,
    splitContainerRef, dividerRef, hideHint, onDividerMouseDown,
  } = useSplitPane({
    canSplit: tier !== 'phone' && (state?.generated ?? false),
    activeView,
    onSwitchTab: _onSwitchTab,
  });

  // Warm the lazily-loaded wizard chunk once the page has settled, so opening
  // Generate / Regenerate later doesn't wait on a download.
  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    const prefetch = () => { void loadGenerateWizard(); };
    if (w.requestIdleCallback) w.requestIdleCallback(prefetch);
    else setTimeout(prefetch, 2000);
  }, []);

  const [debugOpen, setDebugOpen] = useState(true);
  const [debugMatrices, setDebugMatrices] = useState<DebugMatrices | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugError, setDebugError] = useState<string | null>(null);

  // Reset history selection when new state arrives
  useEffect(() => {
    setSelectedHistoryIndex(null);
  }, [state]);

  // Dev mode: fetch debug matrices whenever state changes
  useEffect(() => {
    if (!isDevUser || !state?.generated) return;
    setDebugLoading(true);
    apiClient
      .getDebugMatrices()
      .then((data) => {
        setDebugMatrices(data);
        setDebugError(null);
      })
      .catch((err: Error) => {
        setDebugMatrices(null);
        setDebugError(err.message);
      })
      .finally(() => setDebugLoading(false));
  }, [isDevUser, state?.generated, state?.history?.length]);

  // Browsing a past version: its grid (and the one before it, for the diff) is fetched
  // by id when picked — the state carries version headers only. Until it lands the
  // current grid stays up.
  const [selectedVersion, versionBeforeSelected] = useVersions([
    selectedHistoryIndex !== null ? state?.history[selectedHistoryIndex]?.vid : null,
    selectedHistoryIndex !== null && selectedHistoryIndex > 0 ? state?.history[selectedHistoryIndex - 1]?.vid : null,
  ]);
  const displayedSnapshot = state
    ? (selectedHistoryIndex !== null && selectedVersion) || state.current_snapshot
    : null;

  // Officers actually on the roster at the ACTIVE version — the schedule the grid is
  // drawn from is the only membership record that stays right through undo/redo/delete,
  // so the Add Officers drawer's duplicate check is fed from it rather than from a
  // metadata list that can still name an officer an undo took off the board.
  // Deliberately the active version, not `displayedSnapshot`: browsing an old version
  // in the history dropdown must not change what counts as already added.
  const rosterOfficerMetadata = useMemo(() => {
    const meta = state?.officer_metadata;
    const onRoster = state?.current_snapshot?.officer_schedules;
    if (!meta || !onRoster) return meta;
    return Object.fromEntries(Object.entries(meta).filter(([key]) => key in onRoster));
  }, [state?.officer_metadata, state?.current_snapshot]);

  // The user's per-slot erase/break choices from the officer grid (persisted in
  // sessionStorage). Folded into the unassigned-time detection so the Auto-assign count,
  // the modal's candidates, and the backend fill all see exactly what the grid paints green.
  const schedulesForOverrides = displayedSnapshot?.officer_schedules;
  const breakOverrides = useMemo(
    () => (schedulesForOverrides
      ? readBreakOverrides(schedulesForOverrides, state?.shift_type ?? 'morning')
      : {}),
    // Re-read when the schedule identity changes — every edit (including erasing a break,
    // which also deletes any counter there) produces a fresh snapshot.
    [schedulesForOverrides, state?.shift_type],
  );

  // When viewing a history version, compute diff traces vs the previous version (like Google Docs).
  // Indigo (#6366f1) for all history diff traces.
  const historyTraces = useMemo(
    () => (selectedHistoryIndex === null ? [] : gridTraces(
      versionBeforeSelected?.counter_matrix, selectedVersion?.counter_matrix, '#6366f1')),
    [selectedHistoryIndex, selectedVersion, versionBeforeSelected]);

  // Merge live edit traces with history diff traces; history traces take priority when viewing history.
  // A live Compare Versions selection outranks both — it's a deliberate user pick to inspect.
  const effectiveTraces = compareTraces ?? (selectedHistoryIndex !== null ? historyTraces : (editTraces ?? []));

  // Colorize incoming presence entries for the canvas overlay — hidden while browsing
  // history, same as edit traces, since presence reflects live state, not the past.
  const effectivePresence = useMemo(() => {
    if (selectedHistoryIndex !== null || !cellPresence?.length) return [];
    return cellPresence.map(p => ({
      row: p.row,
      col: p.col,
      officerKey: p.officerKey,
      color: usernameColor(p.userName),
      userName: p.userName,
    }));
  }, [cellPresence, selectedHistoryIndex]);

  // Full name reveal when a presence avatar bubble is clicked on the canvas
  const [presenceNameHint, setPresenceNameHint] = useState<{ x: number; y: number; userName: string } | null>(null);
  const handlePresenceBubbleClick = useCallback((userName: string, clientX: number, clientY: number) => {
    setPresenceNameHint({ x: clientX, y: clientY, userName });
  }, []);

  const currentVersionMessages = [...operationMessages].reverse();
  // Message keys are built from the index in the append-only operationMessages array
  // (not the reversed/newest-first list), so a given message's key never changes as
  // later messages are added — otherwise every existing message's read/expanded state
  // would appear to reset each time a new one arrives.
  // Badge count: only cautioned messages the user hasn't opened-and-closed yet.
  // Clean log entries are read on arrival and never contribute.
  const unreadCautionCount = operationMessages.filter((m, i) => {
    const key = `${m.type}-${m.timestamp}-${i}`;
    return messageHasCaution(m) && !readMsgKeys.has(key);
  }).length;

  const unavailableMask = useMemo(() => {
    const mask = new Set<string>();
    if (!state?.unavailable_periods) return mask;
    const startHour = getStartHour(state?.shift_type ?? "morning");
    for (const p of state.unavailable_periods) {
      const row = p.counter - 1;
      const start = hmmmToSlot(p.start_time, startHour);
      const end = hmmmToEndSlot(p.end_time, startHour);
      for (let col = start; col < end; col++) {
        mask.add(`${row},${col}`);
      }
    }
    return mask;
  }, [state?.unavailable_periods, state?.shift_type]);

  // Per-segment captions for the grids: the saved ones, with the open popup's own
  // rows laid over the officer being edited so a note shows the moment it is typed
  // rather than after the save lands. The popup's rows are that officer's complete
  // set, so clearing a note removes it here too.
  const shiftType = state?.shift_type;
  const effectiveAssignmentNotes = useMemo(() => {
    const saved = displayedSnapshot?.assignment_notes;
    if (!liveAssignmentPreview) return saved;
    const startHour = getStartHour(shiftType ?? "morning");
    const live: Record<string, string> = {};
    for (const row of liveAssignmentPreview.assignments) {
      if (!row.notes) continue;
      try {
        const startSlot = hmmmToSlot(row.startTime, startHour);
        const endSlot = hmmmToEndSlot(row.endTime, startHour);
        for (let slot = startSlot; slot < endSlot; slot++) live[String(slot)] = row.notes;
      } catch { /* skip a row whose times don't parse */ }
    }
    // Vacant (no-counter) ranges carry notes too — not AssignmentRows, so they ride
    // alongside rather than inside `assignments`. Without these, opening the popup
    // would replace this officer's entire note map with one missing every vacant
    // note, saved ones included, until the popup closes again.
    for (const range of liveAssignmentPreview.vacantNotes ?? []) {
      try {
        const startSlot = hmmmToSlot(range.start, startHour);
        const endSlot = hmmmToEndSlot(range.end, startHour);
        for (let slot = startSlot; slot < endSlot; slot++) live[String(slot)] = range.note;
      } catch { /* skip a range whose times don't parse */ }
    }
    return { ...(saved ?? {}), [liveAssignmentPreview.officerId]: live };
  }, [displayedSnapshot?.assignment_notes, liveAssignmentPreview, shiftType]);

  // The breaks the roster PDF prints, per main officer: the grid draws exactly these, not a guess from the counters.
  const rosterTemplate = state?.last_inputs?.roster_template;
  const rosterDuties = state?.last_inputs?.roster_duties;
  const printedBreaks = useMemo(() => printedBreakSlots(rosterTemplate, rosterDuties), [rosterTemplate, rosterDuties]);

  // The duties with a row of their own under the counters ("B@M CTRL BOOTH"): this user's choice
  // (Settings → Non-counter deployments), plus the booth wherever the roster has it.
  const dutyOptions = useDutyOptions();
  // The other car's officers on the booth are listed too (read off the same PDF at generation) so the row
  // is complete whichever car this session is — they are not on this roster, so nothing opens or edits them.
  const otherZone = state?.last_inputs?.other_zone_duties;
  const dutyRows = useMemo(() => {
    const slots = displayedSnapshot?.duty_slots;
    const others = otherZone ?? [];
    if (!slots && others.length === 0) return [];
    const present = new Set([
      ...Object.values(slots ?? {}).flatMap((m) => Object.values(m)),
      ...others.map((d) => d.label),
    ]);
    // A deployment the user chose to bring over from the other car gets a row even if it is not one of this
    // session's listed duties.
    const labels = dutyRowLabels(dutyOptions, present);
    for (const d of others) if (!labels.includes(d.label)) labels.push(d.label);
    return buildDutyRows(slots, labels, others);
  }, [displayedSnapshot?.duty_slots, dutyOptions, otherZone]);

  // Cell colours are baked into gridData, so it is rebuilt when the user edits their OT colours.
  const otColorVersion = useOtColorVersion();
  // Keyed on exactly the parts it is built from, not the whole state: every echo and
  // response hands over a new state object, and rebuilding here repaints both grids.
  // Unchanged parts keep their identity across updates (see shareState).
  const counterMatrix = displayedSnapshot?.counter_matrix;
  const officerMetadata = state?.officer_metadata;
  const operationMode = state?.operation_mode;
  const initialSchedules = state?.initial_officer_schedules ?? undefined;
  const gridData = useMemo(() => {
    void otColorVersion; // dependency only: buildGridData reads the live OT colours
    if (!counterMatrix || !officerMetadata) return null;
    return buildGridData(
      counterMatrix,
      officerMetadata,
      getStartHour(shiftType ?? "morning"),
      operationMode,
      unavailableMask,
      48,
      initialSchedules,
      effectiveAssignmentNotes,
      displayedSnapshot?.duty_slots,
    );
  }, [counterMatrix, officerMetadata, shiftType, operationMode, initialSchedules, unavailableMask, effectiveAssignmentNotes, displayedSnapshot?.duty_slots, otColorVersion]);

  // Apply live assignment preview so the counter grid updates instantly on popup edits
  const effectiveGridData = useMemo(() => {
    if (!gridData || !liveAssignmentPreview || !officerMetadata) return gridData;
    return patchGridDataForPreview(
      gridData,
      liveAssignmentPreview.officerId,
      liveAssignmentPreview.assignments,
      officerMetadata,
      getStartHour(shiftType ?? "morning"),
    );
  }, [gridData, liveAssignmentPreview, officerMetadata, shiftType]);

  const openDrawer = (subTab: 'main' | 'sos' | 'ot') => {
    // Every entry point (tab bar, split-mode sibling, the fixed scroll-away
    // overlay) funnels through here — gating it once covers all of them.
    if (!canEdit) {
      notifyWarning(VIEW_ONLY_MESSAGE);
      return;
    }
    setDrawerSubTab(subTab);
    setDrawerOpen(true);
    setEditedOfficer(null);
    setOfficerGridCloseSignal(s => s + 1);
  };

  const handlePinOfficer = (officerId: string) => {
    setPinnedOfficerIds(prev => prev.includes(officerId) ? prev : [...prev, officerId]);
    if (editedOfficer?.officer === officerId) {
      setPinnedClickedTimes(prev => ({ ...prev, [officerId]: editedOfficer.time ?? '' }));
    }
  };

  const handleUnpinOfficer = (officerId: string) => {
    setPinnedOfficerIds(prev => prev.filter(id => id !== officerId));
    setPinnedClickedTimes(prev => { const next = { ...prev }; delete next[officerId]; return next; });
  };

  const handleClosePanel = useCallback(() => {
    setEditedOfficer(null);
    setPinnedOfficerIds([]);
    setPinnedClickedTimes({});
    setPanelExpanded(false);
    setLiveAvailability(null);
  }, []);

  // Mutual exclusion: close drawer + officer grid popups whenever edit popup opens
  useEffect(() => {
    if (editedOfficer?.officer) {
      setDrawerOpen(false);
      setOfficerGridCloseSignal(s => s + 1);
    }
  }, [editedOfficer?.officer]);

  // Grid-mutation callback for RosterGrid/OfficerScheduleGrid/OfficerEditPopup:
  // the mutation response already carries the post-edit snapshot, so patch the
  // grid with it immediately instead of waiting on a second GET /state round trip.
  // officer_metadata/counts/history are reconciled from this tab's own WebSocket
  // echo of the edit rather than a second full-state fetch. Without a snapshot to
  // patch from (a failed save that rolled back, a superseded one) the grid itself
  // needs the server's view, so that case still refetches.
  const handleMutated = useCallback((result?: Pick<OperationResult, 'snapshot'>) => {
    // An open (or pinned) officer card's live preview was built before this edit; left in place it
    // paints that officer's OLD counters over the grid, so a cell assigned on the Counter tab
    // looked empty until a refresh. The saved state is what the grid shows now.
    setLiveAssignmentPreview(null);
    patchFromOperation(result);
    if (result?.snapshot) reconcileAfterEdit();
    else refetch();
  }, [patchFromOperation, reconcileAfterEdit, refetch]);

  // Called after SOS/OT officers are added. The mutation response already carries the
  // post-add snapshot (including auto-assigned counters), so patch the grid with it
  // immediately instead of waiting on a second GET /state round trip — same pattern as
  // handleMutated, including how officer_metadata/counts/history are reconciled.
  const handleAdded = useCallback((result?: Pick<OperationResult, 'snapshot'>) => {
    setLiveAssignmentPreview(null);
    patchFromOperation(result);
    if (result?.snapshot) reconcileAfterEdit();
    else refetch();
    if (isDevUser) {
      apiClient
        .getDebugMatrices()
        .then((data) => { setDebugMatrices(data); setDebugError(null); })
        .catch((err: Error) => setDebugError(err.message));
    }
  }, [patchFromOperation, reconcileAfterEdit, refetch, isDevUser]);

  const {
    statsCopied, statsExpanded, setStatsExpanded, statsPlacement, setStatsPlacement,
    statsFontPx, setStatsFontPx, startAuto, nowStart, statsFilter, setStatsFilter, useNowAsStart,
    statsTimeMarkers, pivotedStats, zoneStatEntries, liveStatsMarker, statsDisplayText, handleCopyStats,
    footerLines, footerInclude, setFooterInclude,
  } = useStatsPanel({ state, displayedSnapshot, accessMode, sessionId: _sessionId });
  const statsFilterOn = !!statsFilter.start || !!statsFilter.end || statsFilter.officerKeys !== null;



  const {
    showExportModal, setShowExportModal, exportData, pdfDownloading, handleExport, handleDownloadPdf,
  } = usePdfExport({ gridData, state, displayedSnapshot, sessionId: _sessionId });

  // Undo: move the server's version pointer back one step (non-destructive). The client
  // already holds every history entry locally (used for the version dropdown/diff view),
  // so jump the grid to it instantly instead of waiting on the round trip — the dedicated
  // endpoint's response still lands via applySnapshot to reconcile and confirm. The pointer
  // update always steps off the latest state (not a value captured here), so back-to-back
  // clicks don't need to wait on each other's round trip either.
  const handleUndo = useCallback(async () => {
    if (!state || !canUndo) return;
    if (!canEdit) { notifyWarning(VIEW_ONLY_MESSAGE); return; }
    setSelectedHistoryIndex(null);
    patchHistoryPointer((idx) => idx - 1);
    try {
      applySnapshot(await apiClient.undoRoster());
    } catch {
      /* already at the earliest version — nothing to undo */
    } finally {
      dismissSpinner();
    }
  }, [state, applySnapshot, patchHistoryPointer, canUndo, dismissSpinner, canEdit, notifyWarning]);

  // "Undo" toast after each of THIS user's own edits. Many edits are quick corrections
  // (move, then move straight back), and a visible way out lets people act without
  // worrying about mistakes. An edit is a new latest history entry whose last line is
  // this user's name (the roster stamps each entry with its author); undo/redo, other
  // people's edits and the first load add no such entry, so they stay silent.
  const lastHistoryLenRef = useRef<number | null>(null);
  useEffect(() => {
    const len = state?.history.length ?? null;
    const prev = lastHistoryLenRef.current;
    lastHistoryLenRef.current = len;
    if (!state || len === null || prev === null || len <= prev) return;
    if ((state.history_index ?? len - 1) !== len - 1) return;
    const lines = (state.history[len - 1].description || '').trim().split('\n');
    const author = lines.length > 1 ? lines[lines.length - 1].trim().toLowerCase() : '';
    if (!author || author !== (myUserName ?? '').trim().toLowerCase()) return;
    if (/^(Initial schedule|Restored version)/i.test(lines[0])) return;
    notify('Roster updated', 'success', { key: 'edit-undo', duration: 7000, action: { label: 'Undo', onClick: () => { void handleUndo(); } } });
  }, [state, myUserName, notify, handleUndo]);

  // Redo: move the version pointer forward one step (available after one or more undos).
  const handleRedo = useCallback(async () => {
    if (!state || !canRedo) return;
    if (!canEdit) { notifyWarning(VIEW_ONLY_MESSAGE); return; }
    setSelectedHistoryIndex(null);
    patchHistoryPointer((idx) => idx + 1);
    try {
      applySnapshot(await apiClient.redoRoster());
    } catch {
      /* already at the latest version — nothing to redo */
    } finally {
      dismissSpinner();
    }
  }, [state, applySnapshot, patchHistoryPointer, canRedo, dismissSpinner, canEdit, notifyWarning]);

  // Restore: apply a past snapshot as the new current version (adds new history entry)
  const handleRestore = useCallback(async () => {
    if (!state || selectedHistoryIndex === null) return;
    if (!canEdit) { notifyWarning(VIEW_ONLY_MESSAGE); return; }
    try {
      applySnapshot(await apiClient.restoreRoster(selectedHistoryIndex, 'restore'));
    } catch {
      // silent
    }
  }, [state, selectedHistoryIndex, applySnapshot, canEdit, notifyWarning]);

  // Ctrl/Cmd+Z undo; Ctrl+Y or Ctrl/Cmd+Shift+Z redo — collaborative via the API.
  // Left alone while typing in a text field so the browser's own text undo works there.
  useEffect(() => {
    if (!state?.generated) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT'
        || (t.tagName === 'INPUT' && !['checkbox', 'radio', 'button'].includes((t as HTMLInputElement).type)))) return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); void handleUndo(); }
      else if ((k === 'z' && e.shiftKey) || (k === 'y' && !e.shiftKey)) { e.preventDefault(); void handleRedo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state?.generated, handleUndo, handleRedo]);

  // Track "Add Officers" button — show fixed overlay when button scrolls off-screen
  useEffect(() => {
    if (!addBtnEl) return;
    const obs = new IntersectionObserver(
      ([entry]) => setAddBtnInView(entry.isIntersecting),
      { threshold: 0 },
    );
    obs.observe(addBtnEl);
    return () => obs.disconnect();
  }, [addBtnEl]);


  // Drawer placement is a device-based default, independent of the roster's own split
  // direction: phone is always a bottom sheet, real desktop (no touch) is always a
  // right panel, and touch tablets (iPad — often 'wide' tier in landscape, same as a
  // desktop monitor, so width alone can't tell them apart) default to bottom sheet but
  // can opt into the right panel via drawerPlacementPref.
  const canChooseDrawerPlacement = tier !== 'phone' && isTouchDevice;
  const drawerIsBottomSheet = tier === 'phone' ? true : canChooseDrawerPlacement ? drawerPlacementPref === 'bottom' : false;
  const statsCoveredByDrawer = drawerOpen && drawerIsBottomSheet;

  // Choosing the right panel on a touch tablet leaves little width for Counter+Officer
  // side by side, so it also switches the roster split to Stack (panes full-width,
  // stacked) instead — the split direction a right-side drawer actually leaves room for.
  const handleChooseDrawerPlacement = useCallback((placement: 'bottom' | 'right') => {
    setDrawerPlacementPref(placement);
    if (placement === 'right') {
      // 'vertical' is this app's internal name for the Stack arrangement (Counter
      // above Officer, each full width) — see the roster-panes flexDirection above.
      setSplitEnabled(true);
      setSplitDirection('vertical');
    }
  }, [setSplitDirection, setSplitEnabled]);


  // Add/remove body class when the multi-card officer panel is open.
  // Split mode: panel is inline (in-flow flex sibling) — no body class needed to push content.
  // Non-split: panel is position:fixed right sidebar — push main-content left.
  useEffect(() => {
    if (panelOfficerIds.length > 0 && !isSplit) {
      document.body.classList.add('edit-panel-open');
      return () => { document.body.classList.remove('edit-panel-open'); };
    }
  }, [panelOfficerIds.length, isSplit, splitDirection]);

  // panelIsBottom: inline panel is at the bottom (horizontal split) — used to position docked overlays above it.
  // panelIsRight: inline panel is on the right (vertical split) — split container donates width.
  const panelIsBottom = panelOfficerIds.length > 0 && isSplit && splitDirection === 'horizontal';
  const panelIsRight = panelOfficerIds.length > 0 && isSplit && splitDirection === 'vertical';

  // Every "bottom up drawer" should treat the docked Manning Stats bar the same
  // way — the Add Officers drawer already did; the officer-card panel bottom-
  // docking in split view (panelIsBottom) is the other case, e.g. pressing a
  // cell to see an officer's card while a bottom sheet is already the norm for
  // that layout. Only panelIsBottom, not phone's own officer-card sheet: on
  // phone the card is comparatively small (30dvh) and doesn't cover the stats
  // block the way a bottom-docked drawer or an inline split panel does.
  const statsCoveredBySheet = statsCoveredByDrawer || panelIsBottom;
  // The side tab shares the right edge with the officer card and the drawer, so it
  // only takes that edge while neither is open — otherwise it falls back to bottom-up.
  const statsSideActive = statsExpanded && statsPlacement === 'side' && tier === 'wide' &&
    !statsCoveredBySheet && panelOfficerIds.length === 0 && !drawerOpen;
  useEffect(() => {
    if (!statsSideActive) return;
    document.body.classList.add('stats-side-open');
    return () => document.body.classList.remove('stats-side-open');
  }, [statsSideActive]);

  // Collapse the raw stats text whenever a bottom sheet starts covering the
  // stats area, so the sheet isn't fighting a wall of numbers for space —
  // the user can still press Stats to expand it back open either way.
  useEffect(() => {
    if (statsCoveredBySheet) setStatsExpanded(false);
  }, [statsCoveredBySheet, setStatsExpanded]);

  // Track actual height of the inline bottom panel so docked overlays sit above it.
  useEffect(() => {
    const el = inlinePanelRef.current;
    if (!el || !panelIsBottom) { setInlinePanelHeight(0); return; }
    const measure = () => { if (inlinePanelRef.current) setInlinePanelHeight(inlinePanelRef.current.getBoundingClientRect().height); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [panelIsBottom, panelOfficerIds.length]);

  // Track actual height of the open Add Officers drawer (bottom-sheet variants only)
  // so the docked Manning Stats bar can pin itself right above it.
  useEffect(() => {
    const el = drawerPanelRef.current;
    if (!el || !statsCoveredByDrawer) { setDrawerHeight(0); return; }
    const measure = () => { if (drawerPanelRef.current) setDrawerHeight(drawerPanelRef.current.getBoundingClientRect().height); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [statsCoveredByDrawer]);

  // Same, but width, for a right-side drawer in side-by-side split (it also
  // grows on maximize, so this can't just be a fixed constant).
  const drawerCoversSideBySide = drawerOpen && !drawerIsBottomSheet && isSplit && splitDirection === 'horizontal';
  useEffect(() => {
    const el = drawerPanelRef.current;
    if (!el || !drawerCoversSideBySide) { setDrawerWidth(0); return; }
    const measure = () => { if (drawerPanelRef.current) setDrawerWidth(drawerPanelRef.current.getBoundingClientRect().width); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [drawerCoversSideBySide]);

  // Only meaningful in Stack view (the divider is horizontal there); a
  // side-by-side split's divider is vertical and has no comparable "bottom edge".
  // Measures from the divider's TOP (not bottom) — the docked stats bar sits
  // `bottom: dividerBottomOffset`, i.e. its own bottom edge lands at this many
  // px above the viewport bottom. Using the divider's bottom edge here put
  // that exact value at the divider's OWN bottom, so the bar's height then
  // extended upward across the whole divider, burying it under a
  // higher-z-index, full-width bar the user could no longer see or drag.
  // Landing on the top edge instead means the bar's bottom meets the
  // divider's top — sitting flush above it with no overlap.
  useEffect(() => {
    if (!isSplit || splitDirection !== 'vertical') { setDividerBottomOffset(0); return; }
    const measure = () => {
      if (!dividerRef.current) return;
      setDividerBottomOffset(Math.max(0, window.innerHeight - dividerRef.current.getBoundingClientRect().top));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (dividerRef.current) ro.observe(dividerRef.current);
    return () => { window.removeEventListener('resize', measure); ro.disconnect(); };
  }, [isSplit, splitDirection, splitRatio, dividerRef]);

  // Lock outer-page scroll when split view is active so panes own their own scroll
  useEffect(() => {
    // Not while hidden: HomeTab stays mounted behind the Settings tab, and the lock
    // would leave that page unscrollable.
    if (isSplit && visible) {
      // On <html> too: mobile browsers still let the root scroll when only
      // <body> is overflow:hidden, and even a few px of page scroll shifts the
      // panes against the screen-pinned Ctr Stats bar.
      document.documentElement.classList.add('split-mode');
      document.body.classList.add('split-mode');
      return () => {
        document.documentElement.classList.remove('split-mode');
        document.body.classList.remove('split-mode');
      };
    }
  }, [isSplit, visible]);

  // Track split container top to compute exact available height (eliminates empty space below).
  // useLayoutEffect runs synchronously before paint so the measured value is ready on first paint.
  //
  // Measured against the DOCUMENT (rect.top + scrollY), with the page scrolled
  // back to the top: split mode locks page scroll, but a reload / bfcache
  // restore on mobile can leave the window scrolled a little, and a
  // viewport-relative top then came out short by that amount — the panes ended
  // that many px above the screen bottom while the Ctr Stats bar (pinned to
  // the screen bottom) sat below them, misaligned with Add Officers.
  // Also re-measured whenever anything above changes size after the first
  // pass (the Dev Mode banner opening/closing, a notice banner appearing or
  // going away): every element that sits before the panes in the document —
  // each earlier sibling at every level up to <body> — plus the ancestors
  // themselves (they resize when a sibling mounts) are watched. Watching
  // <body> alone misses shrinking, since the app root has min-height: 100dvh.
  useLayoutEffect(() => {
    if (!isSplit) return;
    if (window.scrollY !== 0) window.scrollTo(0, 0);
    const update = () => {
      if (!splitContainerRef.current) return;
      const top = splitContainerRef.current.getBoundingClientRect().top + window.scrollY;
      setSplitContainerTop((prev) => (Math.abs(prev - top) < 0.5 ? prev : top));
    };
    update();
    window.addEventListener('resize', update);
    const ro = new ResizeObserver(update);
    for (let n: Element | null = splitContainerRef.current; n && n !== document.body; n = n.parentElement) {
      if (n !== splitContainerRef.current) ro.observe(n);
      for (let sib = n.previousElementSibling; sib; sib = sib.previousElementSibling) ro.observe(sib);
    }
    return () => { window.removeEventListener('resize', update); ro.disconnect(); };
  }, [isSplit, splitContainerRef]);

  // Track officers pane left edge for positioning the fixed overlay in horizontal split
  useEffect(() => {
    const el = officersPaneRef.current;
    if (!el) return;
    const update = () => setOfficersPaneLeft(el.getBoundingClientRect().left);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, [isSplit, splitDirection, splitRatio]);

  // In split mode, grids fill their flex pane containers (height='100%'), so hiding
  // the pane header above them (statsCoveredBySheet, below) already lets them grow —
  // flex:1 on the canvas wrapper takes over the space display:none frees up.
  // Non-split (phone single-pane) uses explicit heights instead, which don't respond
  // to a hidden sibling the same way, so claim the header's height back explicitly.
  const PANE_HEADER_H = 42;
  const headerReclaim = statsCoveredBySheet ? PANE_HEADER_H : 0;
  // Trimmed down from 180/205/215 by shaving header/legend/pane padding
  // elsewhere (see App.tsx header, .main-content top padding, the legend row,
  // and this pane's own bottom padding below) — freed-up space the counter
  // grid can use to fit more rows before it needs any scrolling at all.
  const baseGridOffsetPx = (tier === 'wide' ? 167 : tier === 'tablet-portrait' ? 192 : 202) - headerReclaim;
  const baseGridH = `calc(100dvh - ${baseGridOffsetPx}px)`;
  const OFFICER_CELL_H = 29;
  const GRID_OVERHEAD_PX = 68;
  const numOfficers = displayedSnapshot ? Object.keys(displayedSnapshot.officer_schedules).length : 0;
  const naturalOfficersH = OFFICER_CELL_H * numOfficers + GRID_OVERHEAD_PX;
  const counterGridHeight = isSplit ? '100%' : baseGridH;
  const officersGridHeight = isSplit ? '100%' : `min(${naturalOfficersH}px, ${baseGridH})`;

  // One No Relief view for the pop-up, the NR buttons on both grids and the schedule text (NoReliefContext).
  // The buttons are about the floor now, so they are hidden while a past version is previewed.
  const liveMatrix = state?.generated ? state.current_snapshot?.counter_matrix : undefined;
  const { groups: noReliefGroups, addAck: addNoReliefAck, deleteAck: deleteNoReliefAck } = noRelief;
  const noReliefView = useMemo<NoReliefView>(() => ({
    marks: selectedHistoryIndex === null ? noReliefMarks(noReliefGroups, liveMatrix) : [],
    ends: noReliefEnds(noReliefGroups),
    canEdit,
    toggle: async (mark) => {
      const action = markAction(mark);
      if (!action) return;
      try {
        const c = formatCounterLabel(mark.entry.counter + 1, state?.operation_mode);
        if (action.kind === 'ack') {
          await addNoReliefAck(mark.entry.counter, mark.entry.slot, action.ackKind);
          notifySuccess(action.ackKind === 'relief'
            ? `${c}: officer told relief has arrived — the green R turns white`
            : `${c}: officer told there is no relief — the red NR turns white`);
        } else {
          await deleteNoReliefAck(action.ackId);
          notifyInfo(`${c}: undone — the officer still needs to be told`);
        }
      } catch (e) {
        notifyError(e instanceof Error ? e.message : 'Could not save the No Relief tick');
      }
    },
  }), [noReliefGroups, liveMatrix, selectedHistoryIndex, canEdit, addNoReliefAck, deleteNoReliefAck, notifyError, notifySuccess, notifyInfo, state?.operation_mode]);

  return (
    <NoReliefProvider value={noReliefView}>
    <div>
      {pinForWizard && _sessionId && (
        <PinDialog
          sessionId={_sessionId}
          onCancel={() => {
            setPinForWizard(false);
            // Not unlocking is not abandoning: the wizard steps aside and its draft waits behind "Resume".
            if (wizardOpen && !canEdit) {
              setWizardOpenState(false);
              persistWizardOpen(_sessionId, false);
            }
          }}
          onVerified={(editToken) => {
            setPinForWizard(false);
            setCanEdit(true, editToken);
            setWizardOpenState(true);
            persistWizardOpen(_sessionId, true);
          }}
        />
      )}
      {/* Resume banner — a pass through the wizard the user never finished.
          Shown whether or not a roster exists, because the half-finished pass
          usually HAS generated one at step 3: without this the user comes back
          to a roster that looks complete and no way back into the wizard. */}
      {resumableStep !== null && !wizardOpen && (
        <div style={{ marginBottom: 16 }}>
          <button
            onClick={() => openWizard(true)}
            style={{
              width: "100%",
              padding: "12px 16px",
              background: "#fffbeb",
              border: "1.5px solid #f59e0b",
              borderRadius: 8,
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              textAlign: "left" as const,
              gap: 12,
            }}
          >
            <span style={{ fontSize: 13, color: "#78350f" }}>
              <strong>Resume roster setup here</strong>
              <span style={{ opacity: 0.8 }}> — step {resumableStep} of 4</span>
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#b45309", whiteSpace: "nowrap" }}>
              Resume →
            </span>
          </button>
        </div>
      )}

      {/* Generate button — only shown when no roster exists yet */}
      {!state?.generated && (
        <div className="ui-zoom" style={{ marginBottom: "24px" }}>
          <button
            onClick={() => openWizard(true)}
            style={{
              width: "100%",
              padding: "14px 20px",
              background: "#667eea",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontWeight: 600,
              fontSize: "15px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
            }}
          >
            <span>✨ Generate New Roster</span>
            <span style={{ fontSize: 13, opacity: 0.8 }}>→</span>
          </button>
        </div>
      )}

      {/* Full-screen wizard overlay */}
      {wizardOpen && canEdit && (
        <Suspense fallback={null}>
        <GenerateWizard
          onClose={() => openWizard(false)}
          onGenerated={() => {
            // A fresh generate wipes the roster server-side (state.clear()), so any Inbox
            // entry about officers added to the discarded roster is now stale — clear it
            // rather than let it linger and read as "already added" on the new roster.
            onClearMessages();
            setReadMsgKeys(new Set());
            setExpandedMsgKeys(new Set());
            refetch();
            openWizard(false);
          }}
          lastInputs={state?.last_inputs ?? null}
          officerCounts={state?.officer_counts ?? { main: 0, sos: 0, ot: 0 }}
          generated={state?.generated || false}
          sessionId={_sessionId}
          cachedPdf={cachedPdf}
          onPdfChange={setCachedPdf}
        />
        </Suspense>
      )}

      {/* Dev-mode debug matrices — only visible to admin/qx users */}
      {isDevUser && (
        <div
          style={{
            marginTop: "16px",
            background: "#1e1e2e",
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
          }}
        >
          <div
            onClick={() => setDebugOpen((o) => !o)}
            style={{
              padding: "12px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
              borderRadius: debugOpen ? "8px 8px 0 0" : "8px",
            }}
          >
            <div>
              <h3 style={{ margin: 0, color: "#cdd6f4", fontFamily: "monospace", fontSize: "14px" }}>
                🛠 Dev Mode — Intermediate Matrices
              </h3>
              <div style={{ fontFamily: "monospace", fontSize: 11, color: "#a6adc8", marginTop: 4 }}>
                <span style={{ color: "#89dceb" }}>Deploy:</span>{" "}
                <span style={{ color: "#cdd6f4" }}>{import.meta.env.VITE_GIT_SHA ? import.meta.env.VITE_GIT_SHA.slice(0, 7) : "dev"}</span>{" "}
                {import.meta.env.VITE_GIT_TIME && (
                  <span style={{ color: "#a6adc8" }}>· {(() => {
                    // VITE_GIT_TIME is "YYYY-MM-DD HH:MM UTC" — convert to SGT (UTC+8).
                    const raw = import.meta.env.VITE_GIT_TIME;
                    const d = new Date(raw.replace(' UTC', '+00:00').replace(' ', 'T'));
                    if (isNaN(d.getTime())) return raw;
                    return d.toLocaleString('en-SG', {
                      timeZone: 'Asia/Singapore',
                      year: 'numeric', month: '2-digit', day: '2-digit',
                      hour: '2-digit', minute: '2-digit', hour12: false,
                    }).replace(',', '') + ' SGT';
                  })()}</span>
                )}
              </div>
            </div>
            <span style={{ color: "#a6adc8", fontSize: "12px" }}>{debugOpen ? "▼" : "▶"}</span>
          </div>
          {debugOpen && (
            <div style={{ padding: "0 20px 20px" }}>
              {debugLoading && (
                <p style={{ color: "#a6adc8", fontFamily: "monospace", fontSize: "12px" }}>
                  Loading…
                </p>
              )}
              {!debugLoading && debugMatrices && (
                <>
                  <Copyable
                    label="Counter Matrix (counter × slot)"
                    text={debugMatrices.counter_matrix}
                    rows={12}
                  />
                  <Copyable
                    label="Officer Schedules (officer → counter per slot)"
                    text={debugMatrices.officer_schedules}
                    rows={12}
                  />
                  <Copyable
                    label="Officer Break/Work Matrix — before counter assignment (W=working, B=break, .=unavailable)"
                    text={debugMatrices.officer_break_work}
                    rows={10}
                  />
                </>
              )}
              {!debugLoading && !debugMatrices && (
                <p
                  style={{
                    color: debugError ? "#f38ba8" : "#a6adc8",
                    fontFamily: "monospace",
                    fontSize: "12px",
                  }}
                >
                  {debugError ? `Error: ${debugError}` : "Generate a roster first."}
                </p>
              )}

            </div>
          )}
        </div>
      )}

      {/* Split/Stack view control — portals into the desktop tab row, left of Inbox,
          instead of the separate copies each pane's own header used to carry. */}
      {state?.generated && tier !== 'phone' && splitControlAnchor && createPortal(
        <SplitControlButton
          isSplit={isSplit}
          splitDirection={splitDirection}
          onEnableSplit={() => setSplitEnabled(true)}
          onToggleDirection={() => setSplitDirection(d => d === 'horizontal' ? 'vertical' : 'horizontal')}
        />,
        splitControlAnchor,
      )}

      {/* Inbox button — portals into the tab bar (desktop row / mobile bottom nav) so it
          saves vertical space instead of taking its own row; visible once a roster exists. */}
      {state?.generated && inboxAnchorDesktop && createPortal(
        <InboxTabButton
          variant="desktop"
          unreadCautionCount={unreadCautionCount}
          onClick={() => { setShowInbox(true); setExpandedMsgKeys(new Set()); }}
        />,
        inboxAnchorDesktop,
      )}
      {/* Mobile: these three portal into the "More" popover (see App.tsx) instead of
          the nav row itself — the row only has space for the 3 main tabs + More. */}
      {state?.generated && inboxAnchorMobile && createPortal(
        <InboxTabButton
          variant="mobile-menu"
          unreadCautionCount={unreadCautionCount}
          onClick={() => { setShowInbox(true); setExpandedMsgKeys(new Set()); onMobileMenuAction?.(); }}
        />,
        inboxAnchorMobile,
      )}

      {/* Notes + Contacts buttons — same portal pattern as Inbox, sit alongside it. */}
      {state?.generated && notesAnchorDesktop && createPortal(
        <IconTabButton variant="desktop" icon="📝" label="Notes" accent="notes" count={filledNotesCount} onClick={() => setShowNotes(true)} />,
        notesAnchorDesktop,
      )}
      {state?.generated && notesAnchorMobile && createPortal(
        <IconTabButton variant="mobile-menu" icon="📝" label="Notes" accent="notes" count={filledNotesCount} onClick={() => { setShowNotes(true); onMobileMenuAction?.(); }} />,
        notesAnchorMobile,
      )}
      {state?.generated && contactsAnchorDesktop && createPortal(
        <IconTabButton variant="desktop" icon={<WaIcon size={14} />} label="Contacts" accent="contacts" onClick={() => setShowContacts(true)} />,
        contactsAnchorDesktop,
      )}
      {state?.generated && contactsAnchorMobile && createPortal(
        <IconTabButton variant="mobile-menu" icon={<WaIcon size={17} />} label="Contacts" accent="contacts" onClick={() => { setShowContacts(true); onMobileMenuAction?.(); }} />,
        contactsAnchorMobile,
      )}

      {/* No Relief button — same portal pattern again. */}
      {state?.generated && noReliefAnchorDesktop && createPortal(
        <NoReliefTabButton variant="desktop" pendingCount={noReliefAlert === 'off' ? 0 : noRelief.pendingCount} onClick={() => setShowNoRelief(true)} />,
        noReliefAnchorDesktop,
      )}
      {state?.generated && noReliefAnchorMobile && createPortal(
        <NoReliefTabButton variant="mobile-menu" pendingCount={noReliefAlert === 'off' ? 0 : noRelief.pendingCount} onClick={() => { setShowNoRelief(true); onMobileMenuAction?.(); }} />,
        noReliefAnchorMobile,
      )}
      {state?.generated && (
        <NoReliefReminder
          groups={noRelief.groups}
          counterMatrix={state.current_snapshot?.counter_matrix}
          officerMetadata={state.officer_metadata}
          operationMode={state.operation_mode}
          shiftType={state.shift_type}
          suppressed={showNoRelief || noReliefAlert !== 'popup'}
          onAck={noRelief.addAck}
        />
      )}
      {showNoRelief && state?.generated && (
        <NoReliefModal
          groups={noRelief.groups}
          operationMode={state.operation_mode}
          startHour={getStartHour(state.shift_type ?? "morning")}
          canEdit={canEdit}
          onAck={noRelief.addAck}
          onUndoAck={noRelief.deleteAck}
          onClose={() => setShowNoRelief(false)}
        />
      )}

      {showNotes && <NotesModal onClose={() => setShowNotes(false)} liveUpdateTick={notesUpdateTick} />}
      {showContacts && <ContactsModal onClose={() => setShowContacts(false)} />}

      {/* Roster visualization */}
      {state?.generated && displayedSnapshot ? (
        <>
              {/* Outer wrapper: owns height budget and flex axis so panes + inline panel share space equally */}
          <div style={{
            height: isSplit ? `calc(100dvh - ${splitContainerTop > 0 ? splitContainerTop : 125}px)` : undefined,
            display: isSplit ? 'flex' : undefined,
            flexDirection: isSplit ? (splitDirection === 'horizontal' ? 'column' : 'row') : undefined,
            overflow: isSplit ? 'hidden' : undefined,
            // A right-side Add Officers drawer is position:fixed (it needs that for
            // its slide-in animation and to work the same way outside split view),
            // so it can't donate width as a flex sibling the way the inline edit
            // panel does. Reserving its measured width here instead — shrinking
            // both panes to make room — is what stops it just sitting on top of
            // whichever pane it happens to cover.
            paddingRight: drawerCoversSideBySide ? `${drawerWidth}px` : undefined,
          }}>
          {/* ── Roster panes: split side-by-side on tablet/desktop, single on phone ── */}
          <div
            ref={splitContainerRef}
            style={{
              display: isSplit ? 'flex' : 'block',
              flexDirection: isSplit ? (splitDirection === 'vertical' ? 'column' : 'row') : undefined,
              flex: isSplit ? '1 1 0' : undefined,
              minWidth: 0,
              minHeight: 0,
              gap: 0,
              alignItems: isSplit ? 'stretch' : undefined,
              overflow: isSplit ? 'hidden' : undefined,
            }}
          >

            {/* ── Counter pane (always shown in split; shown on phone when activeView=counter) ── */}
            {(isSplit || activeView === 'counter') && gridData && (
              <div style={{
                flex: isSplit ? `0 0 calc(${splitRatio * 100}% - 5px)` : undefined,
                minWidth: 0,
                minHeight: 0,
                display: isSplit ? 'flex' : undefined,
                flexDirection: isSplit ? 'column' : undefined,
                position: 'relative',
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                overflow: 'hidden',
              }}>
                {isSplit && hideHint === 'counter' && <HidePaneHint name="Counter" />}

                {/* Counter pane header — hidden while a bottom-up sheet (Add Officers
                    drawer, or the officer card bottom-docked in split view) covers it,
                    to give the grid the extra height, leaving just the grid + the docked Copy bar. */}
                <div style={{ padding: tier === 'phone' ? '3px 8px 2px' : '3px 12px 2px', borderBottom: '1px solid #f0f4f8', display: statsCoveredBySheet ? 'none' : undefined, zoom: UI_SCALE }}>
                  {/* Row 1: title + controls */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: tier === 'phone' ? 4 : 6, overflowX: 'auto' }}>
                    <span title="Counter" style={{ fontWeight: 700, fontSize: '13px', color: '#1e293b', flexShrink: 0 }}>{tier === 'phone' ? '📋' : '📋 Counter'}</span>
                    <div style={{ flex: 1, minWidth: 8 }} />
                    <OfficerSearch value={officerSearch} onChange={setOfficerSearch} />
                    <GridDensitySelect />
                    {/* Version / Compare / Undo / Redo are always present — a
                        one-version roster shows them greyed out rather than
                        rearranging the toolbar the moment a second version
                        exists. The version chip itself stays clickable so the
                        history panel opens even on version 1. */}
                    <button
                      onClick={() => setShowHistory(h => !h)}
                      title="Version history"
                      style={{ background: showHistory ? '#667eea' : 'none', color: showHistory ? 'white' : '#64748b', border: '1px solid #e2e8f0', borderRadius: 6, padding: tier === 'phone' ? '3px 7px' : '3px 9px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontWeight: selectedHistoryIndex !== null ? 600 : 400, flexShrink: 0 }}
                    >
                      🕐 v{(selectedHistoryIndex ?? historyIdx) + 1}
                      {selectedHistoryIndex !== null && (
                        <span style={{ opacity: 0.75, fontSize: 10 }}>· {displayedSnapshot.timestamp}</span>
                      )}
                    </button>

                    {/* Compare sits beside the version chip rather than inside the
                        history dropdown — buried one click down, nobody found it. */}
                    <button
                      onClick={() => { setShowHistory(false); setShowCompare(true); }}
                      title={canCompare ? 'Compare two versions side by side' : 'Compare needs a second version — make an edit first'}
                      disabled={!canCompare}
                      aria-disabled={!canCompare}
                      style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: tier === 'phone' ? '3px 7px' : '3px 9px', fontSize: 12, cursor: canCompare ? 'pointer' : 'default', color: '#64748b', fontWeight: 400, display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, opacity: canCompare ? 1 : 0.4 }}
                    >
                      {tier === 'phone' ? '⇄' : '⇄ Compare'}
                    </button>

                    <HistoryIconButton
                      icon="↩" label="Undo" title="Undo last change"
                      enabled={canUndo} onClick={() => { void handleUndo(); }}
                    />
                    <HistoryIconButton
                      icon="↪" label="Redo" title="Redo last undone change"
                      enabled={canRedo} onClick={() => { void handleRedo(); }}
                    />
                    {selectedHistoryIndex !== null && (
                      <HistoryIconButton
                        icon="♻" label="Restore" title="Restore this version as new current"
                        tone="positive" enabled onClick={() => { void handleRestore(); }}
                      />
                    )}
                    <div style={{ flexShrink: 0 }}>
                      <button ref={menuBtnRef} onClick={() => setShowMenu(m => !m)} title="More actions" style={{ background: showMenu ? '#f1f5f9' : 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 10px', fontSize: 15, cursor: 'pointer', color: '#64748b', lineHeight: 1 }}>⋯</button>
                    </div>
                  </div>
                </div>

                {/* History dropdown */}
                {showHistory && state.history.length > 0 && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 200 }} onClick={() => setShowHistory(false)} />
                    <div style={{ position: 'absolute', top: 42, right: 8, width: Math.min(320, window.innerWidth - 24), maxHeight: 360, overflowY: 'auto', background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 201, padding: 8 }}>
                      {[...state.history].reverse().map((snap, revIdx) => {
                        const idx = state.history.length - 1 - revIdx;
                        const isLatest = idx === state.history.length - 1;
                        const isCurrent = idx === historyIdx;
                        const isSelected = selectedHistoryIndex === idx || (selectedHistoryIndex === null && isCurrent);
                        const parts = snap.description.split('\n');
                        const hasUser = parts.length > 1;
                        const username = hasUser ? parts[parts.length - 1].trim() : null;
                        const desc = hasUser ? parts.slice(0, -1).join('\n') : parts[0];
                        return (
                          <div key={idx} onClick={() => { setSelectedHistoryIndex(isCurrent ? null : idx); setShowHistory(false); }} style={{ padding: '8px 10px', borderRadius: 6, cursor: 'pointer', background: isSelected ? '#667eea' : 'transparent', color: isSelected ? 'white' : '#1e293b', marginBottom: 2 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 2 }}>v{idx + 1}{isLatest ? ' · Latest' : ''} · {snap.timestamp}</div>
                            {/* Batch edits (the roster's own auto-assign names every
                                officer it placed) need more than a headline to be
                                worth reading — the entry wraps rather than clipping. */}
                            <div style={{ fontSize: 11, opacity: 0.85, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>{desc.slice(0, 240)}{desc.length > 240 ? '…' : ''}</div>
                            {username && (
                              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                                <span style={{ background: usernameColor(username), color: 'white', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 600 }}>{username}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Counter canvas — extra bottom padding when the pinned stats toolbar
                    (always fixed to the bottom of the screen in this layout — see its
                    own render below) would otherwise sit over the grid's last rows once
                    the user scrolls all the way down. */}
                <div style={{ flex: isSplit ? (splitDirection === 'vertical' ? '2 1 0' : '1 1 0') : undefined, minHeight: isSplit ? 0 : undefined, minWidth: 0, padding: '8px', paddingBottom: (!isSplit && activeView === 'counter' && displayedSnapshot?.stats) ? Math.max(tier === 'phone' ? 44 : 48, dockBarH + 8) : (isSplit && statsExpanded && !statsSideActive ? Math.max(8, dockBarH - SPLIT_FOOTER_H + 8) : 8) }}>
                  <RosterGrid
                    gridData={effectiveGridData ?? gridData!}
                    operationMode={state.operation_mode}
                    shiftType={state.shift_type ?? "morning"}
                    height={counterGridHeight}
                    growToFitRows={!isSplit}
                    revealRequest={revealRequest}
                    unavailablePeriods={state.unavailable_periods ?? []}
                    onUpdated={handleMutated}
                    onOptimisticClear={optimisticClearOfficerRanges}
                    onOptimisticMove={moveBlockOptimistically}
                    onOfficerClick={setEditedOfficer}
                    highlightOfficerIds={effectiveHighlightIds}
                    callBlocks={selectedHistoryIndex === null ? callBlocks : undefined}
                    callAcked={callAcks.acked}
                    onToggleCall={toggleBlockCall}
                    pivotStats={pivotedStats.length > 0 ? pivotedStats : undefined}
                    zoneStats={zoneStatEntries.length > 0 ? zoneStatEntries : undefined}
                    editTraces={effectiveTraces}
                    presence={effectivePresence}
                    onPresenceBubbleClick={handlePresenceBubbleClick}
                    dutyRows={dutyRows}
                  />
                </div>

                {/* Manning stats — Stack view (vertical split) only: text + filter/copy
                    fit inline as a flex row, no scrolling-off-screen concern since this
                    pane doesn't grow taller than its viewport slot the way phone/tablet
                    single-pane view can. Every other layout shows stats via the single
                    pinned toolbar fixed to the bottom of the screen (below), which is
                    always visible regardless of scroll position — see its own comment. */}
                {displayedSnapshot.stats && isSplit && splitDirection === 'vertical' && (
                  <div style={{
                    display: statsCoveredBySheet ? 'none' : 'flex',
                    flex: '1 1 0', flexShrink: 1, flexDirection: 'row', gap: 8,
                    overflow: 'hidden', minHeight: 0,
                    borderTop: '1px solid #f0f4f8', padding: '8px 12px 10px',
                  }}>
                    {/* Left (2 parts): the raw stats text, full height */}
                    <pre style={{ flex: 2, minWidth: 0, minHeight: 0, margin: 0, padding: '6px 8px', background: '#f8fafc', borderRadius: '4px', fontSize: `${statsFontPx}px`, fontFamily: 'monospace', lineHeight: '1.5', color: '#374151', overflowX: 'auto', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                      <LiveStatsText text={statsDisplayText} marker={liveStatsMarker} />
                      <StatsFooterLines lines={footerLines} include={footerInclude} onChange={setFooterInclude} fontPx={statsFontPx} />
                    </pre>
                    {/* Right (1 part): filter at min height on top, Copy Stats filling the rest */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {tier === 'wide' && <StatsFontControls size={statsFontPx} onChange={setStatsFontPx} />}
                      {statsTimeMarkers.length > 0 && (
                        <StatsFilterPopover
                          officerSchedules={displayedSnapshot?.officer_schedules ?? {}}
                          officerMetadata={state?.officer_metadata ?? {}}
                          timeLabels={statsTimeMarkers}
                          value={statsFilter}
                          onChange={setStatsFilter}
                          nowStart={nowStart}
                          startIsNow={startAuto && !!nowStart}
                          onUseNow={useNowAsStart}
                        />
                      )}
                      <button onClick={handleCopyStats} style={{ flex: 1, width: '100%', minHeight: 0, padding: '6px 8px', fontSize: '12px', borderRadius: '6px', border: 'none', background: statsCopied ? '#22c55e' : '#667eea', color: 'white', cursor: 'pointer', fontWeight: 700 }}>
                        {statsCopied ? '✓ Copied!' : '📊 Copy Stats'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Drag divider — only in split mode ── */}
            {isSplit && (
              <div
                ref={dividerRef}
                onMouseDown={onDividerMouseDown}
                className="split-divider"
                data-dir={splitDirection}
                title="Drag to resize · pull past the edge to hide a pane"
              >
                <div className="split-divider__grip" />
              </div>
            )}

            {/* ── Officers pane (always shown in split; shown on phone when activeView=officers) ── */}
            {(isSplit || activeView === 'officers') && (
              <div ref={officersPaneRef} style={{ position: 'relative', flex: isSplit ? '1 1 0' : undefined, minWidth: 0, minHeight: 0, display: isSplit ? 'flex' : undefined, flexDirection: isSplit ? 'column' : undefined, background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                {isSplit && hideHint === 'officers' && <HidePaneHint name="Officers" />}

                {/* Officers pane header — hidden while a bottom-up sheet covers it, same
                    as the Counter pane header, so its grid gets the height back too. */}
                <div style={{ padding: tier === 'phone' ? '4px 8px' : '4px 12px', borderBottom: '1px solid #f0f4f8', display: statsCoveredBySheet ? 'none' : 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tier === 'phone' ? 4 : 8, position: 'relative', zoom: UI_SCALE }}>
                  {/* On a phone the title is just its icon: the toolbar beside it has to fit Undo/Redo
                      on the same row, and a button pushed off the edge can't be pressed. */}
                  <span title="Officers" style={{ fontWeight: 700, fontSize: '13px', color: '#1e293b', flexShrink: 0 }}>{tier === 'phone' ? '👤' : '👤 Officer'}</span>
                  <div style={{ display: 'flex', gap: tier === 'phone' ? 4 : 6, alignItems: 'center', minWidth: 0 }}>
                    {/* Layout / text size for this grid. Desktop-width screens only — on a phone
                        the grid is already as large as the screen allows. */}
                    {typeof window !== 'undefined' && window.innerWidth >= 900 && <GridDensitySelect view="officer" />}

                    {/* End-of-shift HOTO message (OT officers' notes are typed in the
                        grid's HOTO note column, right of the last time slot). */}
                    {displayedSnapshot && (
                      <button
                        onClick={() => setShowHoto(true)}
                        title="Build the end-of-shift HOTO message to send to the next car field"
                        style={{
                          background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 20,
                          padding: tier === 'phone' ? '4px 9px' : '4px 11px', fontSize: 12, cursor: 'pointer', color: '#92400e',
                          fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
                        }}
                      >
                        {tier === 'phone' ? 'HOTO' : 'HOTO msg'}
                      </button>
                    )}

                    {/* Shared auto-assign — jointly fills unassigned slots across officers.
                        Always enabled: when nothing is unassigned yet, it opens the "Clear
                        Counters" picker instead so the user can free up the slots they want
                        re-planned, then chains straight into this same auto-assign modal —
                        one button covers "auto-assign now" and "clear then auto-assign". */}
                    {(() => {
                      const unassignedCount = displayedSnapshot
                        ? officersWithUnassignedTime(displayedSnapshot.officer_schedules, state.officer_metadata, state.shift_type ?? 'morning', breakOverrides, displayedSnapshot.duty_slots).length
                        : 0;
                      const hasUnassigned = unassignedCount > 0;
                      return (
                        <button
                          onClick={() => {
                            if (!canEdit) { notifyWarning(VIEW_ONLY_MESSAGE); return; }
                            setShowSharedAutoAssign(true);
                          }}
                          title={!canEdit
                            ? 'View only — switch to Edit in the header to auto-assign'
                            : hasUnassigned
                              ? `Auto-assign the unassigned slots of ${unassignedCount} officer(s) together (shared optimum), and free up more counter time in the same batch`
                              : 'No unassigned slots yet — free up the counter time you want re-planned and auto-assign fills it back in, as one edit'}
                          style={{
                            background: '#eef2ff',
                            border: '1px solid #c7d2fe',
                            borderRadius: 20, padding: tier === 'phone' ? '4px 9px' : '4px 11px', fontSize: 12,
                            cursor: canEdit ? 'pointer' : 'not-allowed',
                            opacity: canEdit ? 1 : 0.6,
                            color: '#4f46e5', fontWeight: 600,
                            display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
                          }}
                        >
                          {tier === 'phone' ? '⚡ Auto' : '⚡ Auto-assign'}
                        </button>
                      );
                    })()}

                    {/* Reset hidden rows — only shown when rows are hidden */}
                    {hiddenOfficerIds.size > 0 && (
                      <button
                        onClick={() => setHiddenOfficerIds(new Set())}
                        title="Show all hidden officer rows"
                        style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#3b82f6', fontWeight: 600 }}
                      >
                        ↺ {hiddenOfficerIds.size}{tier === 'phone' ? '' : ' hidden'}
                      </button>
                    )}

                    {/* On phone or non-split: show history controls */}
                    {!isSplit && (
                      <>
                        <HistoryIconButton
                          icon="↩" label="Undo" title="Undo last change"
                          enabled={canUndo} onClick={() => { void handleUndo(); }}
                        />
                        <HistoryIconButton
                          icon="↪" label="Redo" title="Redo last undone change"
                          enabled={canRedo} onClick={() => { void handleRedo(); }}
                        />
                      </>
                    )}
                  </div>
                </div>

                {/* Officers canvas */}
                <div style={{ flex: isSplit ? '1 1 0' : undefined, minHeight: isSplit ? 0 : undefined, minWidth: 0, display: isSplit ? 'flex' : undefined, flexDirection: isSplit ? 'column' : undefined, padding: '8px', paddingBottom: (!isSplit && tier === 'phone') ? 40 : 8 }}>
                  <div style={{ flex: isSplit ? '1 1 0' : undefined, minHeight: isSplit ? 0 : undefined, minWidth: 0 }}>
                    <OfficerScheduleGrid
                      officer_schedules={displayedSnapshot.officer_schedules}
                      officer_metadata={state.officer_metadata}
                      time_labels={gridData?.time_labels ?? []}
                      num_slots={48}
                      operation_mode={state.operation_mode ?? undefined}
                      shiftType={state.shift_type ?? "morning"}
                      numCounters={gridData?.num_counters ?? getModeConfig(state.operation_mode).numCounters}
                      height={officersGridHeight}
                      growToFitRows={!isSplit}
                      onUpdated={handleMutated}
                      onOptimisticClear={optimisticClearOfficerRanges}
                      onOptimisticRemove={optimisticRemoveOfficer}
                      closeSignal={officerGridCloseSignal}
                      onInteraction={() => setEditedOfficer(null)}
                      onOfficerClick={setEditedOfficer}
                      hiddenOfficerIds={hiddenOfficerIds}
                      onChangeHiddenIds={(ids) => setHiddenOfficerIds(ids)}
                      highlightOfficerIds={effectiveHighlightIds}
                      callStates={selectedHistoryIndex === null ? callByOfficer : undefined}
                      callBlocks={selectedHistoryIndex === null ? callBlocks : undefined}
                      callAcked={callAcks.acked}
                      onToggleCall={toggleBlockCall}
                      onToggleOfficerCall={toggleOfficerCall}
                      availabilityOverride={liveAvailability}
                      onAvailabilityPreview={(officerId, segs) => setLiveAvailability(segs.length > 0 ? { officerId, segs } : null)}
                      initialOfficerSchedules={initialSchedules}
                      assignmentNotes={effectiveAssignmentNotes}
                      dutySlots={displayedSnapshot.duty_slots}
                      printedBreaks={printedBreaks}
                      endNotes={hotoNotes}
                      onEndNoteChange={(key, text) => setHotoNote(key, text, () => notifyWarning('Could not save the HOTO note'))}
                    />
                  </div>
                  {!isSplit && (
                    <button
                      ref={setAddBtnEl}
                      onClick={() => openDrawer('sos')}
                      style={{
                        display: tier === 'phone' ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                        padding: '6px 12px',
                        background: '#667eea', border: 'none',
                        color: 'white', fontSize: 12, fontWeight: 700,
                        cursor: 'pointer', textAlign: 'center' as const, userSelect: 'none' as const,
                        width: '100%',
                      }}
                    >
                      <span style={{ fontSize: 15, lineHeight: 1, fontWeight: 400 }}>+</span>
                      Add Officers
                    </button>
                  )}
                </div>
                {/* In split mode, Add Officers sits as a flex sibling so it's always visible at the pane bottom */}
                {isSplit && (
                  <button
                    ref={setAddBtnEl}
                    onClick={() => openDrawer('sos')}
                    style={{
                      flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                      // Same 36px row + 1px top rule as the docked Ctr Stats bar beside
                      // it (SPLIT_FOOTER_H), so the two read as one aligned footer.
                      height: SPLIT_FOOTER_H, padding: '0 12px',
                      background: '#667eea', border: 'none', borderTop: '1px solid #dbe1ea',
                      color: 'white', fontSize: 12, fontWeight: 700,
                      cursor: 'pointer', textAlign: 'center' as const, userSelect: 'none' as const,
                      width: '100%',
                    }}
                  >
                    <span style={{ fontSize: 15, lineHeight: 1, fontWeight: 400 }}>+</span>
                    Add Officers
                  </button>
                )}
              </div>
            )}
          </div>
          {/* Inline edit panel — flex sibling so panes donate height (horizontal) or width (vertical) */}
          {(panelIsBottom || panelIsRight) && state?.generated && gridData && (
            <div
              ref={inlinePanelRef}
              onClick={(e) => e.stopPropagation()}
              style={
                panelIsBottom
                  // Fixed height, not content-sized up to a cap: the docked Ctr Stats bar
                  // and the Officers pane's Add Officers button both sit on this panel's
                  // top edge, so a card that grew/shrank with every edit (a break row
                  // appearing after a dropdown pick, a row added/removed) nudged both
                  // of them up and down. Overflow scrolls inside instead.
                  ? { flex: '0 0 auto', height: '40dvh', background: 'white', borderTop: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 -4px 28px rgba(0,0,0,0.18)' }
                  : { flex: '0 0 min(calc(400px * var(--ui-scale, 1)), 100vw)', background: 'white', borderLeft: '1px solid #e5e7eb', boxShadow: '-4px 0 28px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }
              }
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px 5px 12px', borderBottom: '1px solid #e5e7eb', flexShrink: 0, background: 'white' }}>
                <div ref={setHeaderAnchorEl} style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }} />
                <button onClick={handleClosePanel} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#888', lineHeight: 1, padding: '6px 10px', flexShrink: 0 }}>✕</button>
              </div>
              {!canEdit && <ViewOnlyBanner compact />}
              <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>
                {panelOfficerIds.map(officerId => (
                  <OfficerEditPopup
                    key={officerId}
                    asCard={true}
                    breakOverrides={breakOverrides}
                    officerId={officerId}
                    clickedTimeLabel={officerId === activeOfficerId ? (editedOfficer?.time ?? '') : (pinnedClickedTimes[officerId] ?? '')}
                    gridData={gridData}
                    shiftType={state.shift_type ?? "morning"}
                    numCounters={gridData.num_counters ?? getModeConfig(state.operation_mode).numCounters}
                    operation_mode={state.operation_mode ?? "arrival"}
                    onSaved={(result) => { setLiveAssignmentPreview(null); handleMutated(result); }}
                    onClose={() => {
                      if (pinnedOfficerIds.includes(officerId)) {
                        handleUnpinOfficer(officerId);
                      } else {
                        setEditedOfficer(null);
                      }
                      setLiveAvailability(null);
                      setLiveAssignmentPreview(null);
                    }}
                    isPinned={pinnedOfficerIds.includes(officerId)}
                    onPin={() => handlePinOfficer(officerId)}
                    onUnpin={() => handleUnpinOfficer(officerId)}
                    onAvailabilityChange={(segs) => setLiveAvailability(segs.length > 0 ? { officerId, segs } : null)}
                    onPreviewChange={(assignments, vacantNotes) => setLiveAssignmentPreview({ officerId, assignments, vacantNotes })}
                    onRevealCounter={(counterNum, startSlot) => setRevealRequest((r) => ({ row: counterNum - 1, startSlot, nonce: (r?.nonce ?? 0) + 1 }))}
                    initialSchedule={initialSchedules?.[officerId]}
                    currentSchedule={state.current_snapshot?.officer_schedules?.[officerId]}
                    onOptimisticRemove={optimisticRemoveOfficer}
                    onOptimisticClearCounters={optimisticClearOfficerCounters}
                    headerAnchor={panelOfficerIds.length === 1 ? headerAnchorEl : undefined}
                  />
                ))}
              </div>
            </div>
          )}
          </div>{/* end outer wrapper */}
        </>
      ) : (
        /* Only show placeholder when no schedule AND wizard is not open */
        !wizardOpen && (
          <div
            className="ui-zoom"
            style={{
              background: "white",
              borderRadius: "8px",
              padding: "60px",
              textAlign: "center",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <h2>No roster generated yet</h2>
            <p style={{ color: "#666", marginBottom: "24px" }}>
              Use the Generate button above to create a new roster
            </p>
            <Button onClick={() => openWizard(true)}>Generate Roster</Button>
          </div>
        )
      )}


      {/* Multi-card officer panel — position:fixed, only for non-split (phone / single-pane tablet).
          In split mode the panel renders as an inline flex sibling inside the outer wrapper above. */}
      {!isSplit && panelOfficerIds.length > 0 && state?.generated && gridData && (
        <>
          <div className="officer-edit-panel-backdrop" onClick={handleClosePanel} />
          <div
            className={`officer-edit-panel${panelExpanded ? ' expanded' : ''}`}
            style={{}}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Panel outer header: identity/actions row (portaled in from the one card
                below, when there's exactly one — see headerAnchorEl), replacing the old
                "Officer Schedule" label, + expand + close */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px 5px 12px', borderBottom: '1px solid #e5e7eb', flexShrink: 0, background: 'white' }}>
              <div ref={setHeaderAnchorEl} style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }} />
              <button
                className="popup-expand-btn"
                onClick={() => setPanelExpanded(e => !e)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', lineHeight: 1, padding: '0 2px', flexShrink: 0, display: 'flex', alignItems: 'center' }}
              >
                {panelExpanded ? (
                  <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="11,5 7,5 7,1"/><line x1="11" y1="1" x2="7" y2="5"/>
                    <polyline points="1,7 5,7 5,11"/><line x1="1" y1="11" x2="5" y2="7"/>
                  </svg>
                ) : (
                  <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="7,1 11,1 11,5"/><line x1="11" y1="1" x2="6.5" y2="5.5"/>
                    <polyline points="5,11 1,11 1,7"/><line x1="1" y1="11" x2="5.5" y2="6.5"/>
                  </svg>
                )}
              </button>
              <button onClick={handleClosePanel} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#888', lineHeight: 1, padding: '6px 10px', flexShrink: 0 }}>✕</button>
            </div>
            {!canEdit && <ViewOnlyBanner compact />}
            {/* Scrollable card stack */}
            <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>
              {panelOfficerIds.map(officerId => (
                <OfficerEditPopup
                  key={officerId}
                  asCard={true}
                  breakOverrides={breakOverrides}
                  officerId={officerId}
                  clickedTimeLabel={officerId === activeOfficerId ? (editedOfficer?.time ?? '') : (pinnedClickedTimes[officerId] ?? '')}
                  gridData={gridData}
                  shiftType={state.shift_type ?? "morning"}
                  numCounters={gridData.num_counters ?? getModeConfig(state.operation_mode).numCounters}
                  operation_mode={state.operation_mode ?? "arrival"}
                  onSaved={(result) => { setLiveAssignmentPreview(null); handleMutated(result); }}
                  onClose={() => {
                    if (pinnedOfficerIds.includes(officerId)) {
                      handleUnpinOfficer(officerId);
                    } else {
                      setEditedOfficer(null);
                    }
                    setLiveAvailability(null);
                    setLiveAssignmentPreview(null);
                  }}
                  isPinned={pinnedOfficerIds.includes(officerId)}
                  onPin={() => handlePinOfficer(officerId)}
                  onUnpin={() => handleUnpinOfficer(officerId)}
                    initialSchedule={initialSchedules?.[officerId]}
                    currentSchedule={state.current_snapshot?.officer_schedules?.[officerId]}
                  onAvailabilityChange={(segs) => setLiveAvailability(segs.length > 0 ? { officerId, segs } : null)}
                  onPreviewChange={(assignments, vacantNotes) => setLiveAssignmentPreview({ officerId, assignments, vacantNotes })}
                  onRevealCounter={(counterNum, startSlot) => setRevealRequest((r) => ({ row: counterNum - 1, startSlot, nonce: (r?.nonce ?? 0) + 1 }))}
                  onOptimisticRemove={optimisticRemoveOfficer}
                  onOptimisticClearCounters={optimisticClearOfficerCounters}
                  headerAnchor={panelOfficerIds.length === 1 ? headerAnchorEl : undefined}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Shared auto-assign modal — jointly fill unassigned slots across officers */}
      {showHoto && state?.generated && displayedSnapshot && (
        <HotoMessageModal
          operationMode={state.operation_mode}
          officerSchedules={displayedSnapshot.officer_schedules}
          officerMetadata={state.officer_metadata}
          notes={hotoNotes}
          onClose={() => setShowHoto(false)}
        />
      )}
      {showSharedAutoAssign && state?.generated && displayedSnapshot && (
        <SharedAutoAssignModal
          officer_schedules={displayedSnapshot.officer_schedules}
          officer_metadata={state.officer_metadata}
          shiftType={state.shift_type ?? 'morning'}
          timeLabels={gridData?.time_labels ?? []}
          operationMode={state.operation_mode}
          deriveWindows={deriveOfficerScheduleWindows}
          onOptimisticClear={optimisticClearOfficerRanges}
          breakOverrides={breakOverrides}
          dutySlots={displayedSnapshot.duty_slots}
          onClose={() => setShowSharedAutoAssign(false)}
          onDone={(result) => handleMutated(result)}
        />
      )}

      {/* Fixed "Add Officers" overlay — Officers pane / tab only; the Counter tab's
          own Add Officers button lives inside the Stats footer below instead, since
          the Officers pane (and its in-flow button that !addBtnInView tracks) isn't
          even mounted while viewing Counter in non-split mode.
          – Phone Officers tab: always visible (docked flush above bottom nav, no gap)
          – Officers tab / split mode (any tier): shown when the in-flow button has
            scrolled off-screen
            · Vertical split: full-width bar at bottom of screen
            · Horizontal split: bar covering only the officers pane width */}
      {state?.generated &&
       ((tier === 'phone' && activeView === 'officers') ||
        (!isSplit && activeView === 'officers' && !addBtnInView) ||
        (isSplit && !addBtnInView)) && !wizardOpen && (
        <button
          data-dock-bar
          onClick={() => openDrawer('sos')}
          style={{
            position: 'fixed',
            bottom: tier === 'phone'
              ? 'calc(64px + env(safe-area-inset-bottom))'
              : (panelIsBottom && inlinePanelHeight > 0 ? `${inlinePanelHeight}px` : (isSplit ? '0' : '28px')),
            left: (isSplit && splitDirection === 'horizontal') ? `${officersPaneLeft}px` : 0,
            right: 0,
            zIndex: 50,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            padding: tier === 'phone' ? '8px 18px' : '16px 24px',
            background: '#667eea', border: 'none',
            color: 'white', fontSize: 13, fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.12)',
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1, fontWeight: 400 }}>+</span>
          Add Officers
        </button>
      )}

      {/* Add Officers Drawer — right panel on desktop, bottom sheet on phone/tablet
          (tablet can opt into the right panel via the placement toggle below). */}
      <AddOfficersDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        subTab={drawerSubTab}
        onSubTabChange={setDrawerSubTab}
        generated={state?.generated ?? false}
        onAdded={handleAdded}
        onStateRestored={applySnapshot}
        mode={state?.operation_mode}
        shift={state?.shift_type ?? undefined}
        onOperationMessage={onOperationMessage}
        fromBottom={drawerIsBottomSheet}
        canChoosePlacement={canChooseDrawerPlacement}
        placement={drawerPlacementPref}
        onChoosePlacement={handleChooseDrawerPlacement}
        officerMetadata={rosterOfficerMetadata}
        panelRef={drawerPanelRef}
      />

      {showExportModal && exportData && (
        <ExportPreviewModal
          data={exportData}
          sessionId={_sessionId}
          downloading={pdfDownloading}
          onDownload={handleDownloadPdf}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {/* Counter tab footer — one pinned bar (position: fixed, same pattern as the
          Officers-tab "Add Officers" overlay above) always visible at the bottom of
          the screen for every layout except Stack view (handled inline above, since
          that pane never scrolls past its own viewport slot). It never depends on
          scroll position, so it can't disappear while scrolling.
          In the plain non-split case (the normal way of viewing Counter, on phone or
          desktop/tablet with split off) this bar carries the Counter tab's own Add
          Officers button too — same button the Officers tab has, so it "appears in
          both tabs" as one consistent footer — next to a compact Stats toggle.
          Elsewhere (split's counter pane, or the Add Officers drawer already covering
          this area) it's just the Stats toggle: no point merging in Add Officers when
          the Officers pane has its own copy already, or the drawer it opens is
          already open.
          Filter and Copy Stats used to sit in this row permanently, reading as two
          more buttons unrelated to the toggle beside them. They now live inside the
          panel Stats expands open instead — grouped with the text they act on, only
          present while that panel is. */}
      {state?.generated && displayedSnapshot?.stats &&
       ((isSplit && splitDirection === 'horizontal') || (!isSplit && activeView === 'counter') ||
        (statsCoveredBySheet && (isSplit || activeView === 'counter'))) && (() => {
        const mergeAddOfficers = !isSplit && activeView === 'counter' && !statsCoveredBySheet && !wizardOpen;
        return (
        <div
          data-dock-bar
          ref={setDockBarEl}
          style={{
            position: 'fixed',
            bottom: !statsCoveredBySheet
              ? (tier === 'phone' ? 'calc(64px + env(safe-area-inset-bottom))' : (panelIsBottom && inlinePanelHeight > 0 ? `${inlinePanelHeight}px` : 0))
              // Stack view: only the Add Officers drawer can be the covering sheet
              // (the officer card bottom-docks only in side-by-side split) — pin
              // above whichever is lower, the drawer's own top edge or the
              // divider's, so the bar never ends up sitting under the divider
              // the way it used to when it just followed the drawer's height.
              : (isSplit && splitDirection === 'vertical') ? `${Math.max(drawerHeight, dividerBottomOffset)}px`
              : `${statsCoveredByDrawer ? drawerHeight : inlinePanelHeight}px`,
            left: 0,
            // Stops at the Officer pane's left edge in side-by-side split — it belongs
            // to the Counter pane, so it shouldn't stretch across the Officer column
            // even while the (full-width) drawer is covering both underneath.
            right: (isSplit && splitDirection === 'horizontal') ? window.innerWidth - officersPaneLeft : statsSideActive ? 'min(calc(340px * var(--ui-scale, 1)), 100vw)' : 0,
            zIndex: statsCoveredBySheet ? 802 : 51,
            // Muted/neutral vs. the main bottom nav's white — a visual cue (backed up
            // by the smaller height below) that this bar is a secondary, floating
            // action row for the Counter tab, not another tab of equal standing to
            // Counter/Officers/Settings/More underneath it.
            background: '#eef1f6',
            borderTop: '1px solid #dbe1ea',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.09)',
          }}
        >
          {/* On phones the bar floats a safe-area-sized hair above the bottom nav, so
              whatever scrolls underneath (e.g. the legend) showed through that gap.
              This filler runs the bar's background down beneath the nav. */}
          {tier === 'phone' && !statsCoveredBySheet && (
            <div aria-hidden style={{ position: 'absolute', top: '100%', left: 0, right: 0, height: 80, background: '#eef1f6', pointerEvents: 'none' }} />
          )}
          {statsExpanded && (() => {
            const side = statsSideActive;
            const body = (
            <div style={side
              ? { position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(calc(340px * var(--ui-scale, 1)), 100vw)', zIndex: 2001, padding: '10px 12px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, background: 'white', boxShadow: '-4px 0 28px rgba(0,0,0,0.18)' }
              : { borderBottom: '1px solid #dbe1ea', padding: '8px 12px', maxHeight: '34dvh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, background: 'white' }}>
              {/* One pinned header: the title, then the two actions people come here for —
                  Filter and Copy Stats — labelled and always in the same place, even while
                  the stats scroll beneath. Text size and docking are rarely changed, so
                  they wait behind the ⚙ button instead of competing with them. */}
              <div style={{ position: 'sticky', top: 0, zIndex: 2, background: 'white', margin: side ? '-10px -12px 0' : '-8px -12px 0', padding: side ? '10px 12px 6px' : '8px 12px 6px', borderBottom: '1px solid #eef1f6', display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {tier === 'wide' && <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', flexShrink: 0 }}>📊 Ctr Stats</span>}
                  {statsTimeMarkers.length > 0 && (
                    <StatsFilterPopover
                      officerSchedules={displayedSnapshot?.officer_schedules ?? {}}
                      officerMetadata={state?.officer_metadata ?? {}}
                      timeLabels={statsTimeMarkers}
                      value={statsFilter}
                      onChange={setStatsFilter}
                      nowStart={nowStart}
                      startIsNow={startAuto && !!nowStart}
                      onUseNow={useNowAsStart}
                    />
                  )}
                  <div style={{ flex: 1 }} />
                  <button
                    onClick={() => setShowStatsDisplay(v => !v)}
                    aria-label="Display options"
                    aria-expanded={showStatsDisplay}
                    title="Text size and where the stats are docked"
                    style={{ flexShrink: 0, padding: '6px 9px', fontSize: 14, lineHeight: 1, borderRadius: 6, border: '1px solid #e2e8f0', background: showStatsDisplay ? '#f1f5f9' : 'white', color: '#64748b', cursor: 'pointer' }}
                  >
                    ⚙
                  </button>
                  {side && (
                    <button onClick={() => setStatsExpanded(false)} aria-label="Close stats" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, color: '#888', padding: '2px 6px', flexShrink: 0 }}>✕</button>
                  )}
                </div>
                {showStatsDisplay && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <StatsFontControls size={statsFontPx} onChange={setStatsFontPx} />
                    {tier === 'wide' && (
                      <button
                        onClick={() => setStatsPlacement(statsPlacement === 'side' ? 'bottom' : 'side')}
                        title={statsPlacement === 'side' ? 'Dock along the bottom' : 'Dock down the right side — more vertical room'}
                        style={{ padding: '3px 8px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid #e2e8f0', background: 'white', color: '#475569', cursor: 'pointer' }}
                      >
                        {statsPlacement === 'side' ? '⇩ Dock at bottom' : '⇥ Dock at side'}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <pre style={{ margin: 0, padding: '6px 8px', background: '#f8fafc', borderRadius: '4px', fontSize: `${statsFontPx}px`, fontFamily: 'monospace', lineHeight: '1.5', color: '#374151', overflowX: 'auto', whiteSpace: 'pre-wrap' as const }}>
                <LiveStatsText text={statsDisplayText} marker={liveStatsMarker} />
              </pre>
              <StatsFooterLines lines={footerLines} include={footerInclude} onChange={setFooterInclude} fontPx={statsFontPx} />
            </div>
            );
            return side ? createPortal(body, document.body) : body;
          })()}
          {/* Add Officers is this row's one solid-filled button: it is the page's primary
              action, so it is the only thing shouting. Ctr Stats sits beside it in the
              same fixed spot but in a soft tint — still easy to find and wider, it just
              doesn't compete. (Filled-vs-tinted, not two identical blocks, tells the eye
              which one to reach for first.) The chevron shows the panel's current
              state: ▼ while collapsed, ▲ while expanded. */}
          <div style={{ display: 'flex', alignItems: 'stretch', height: SPLIT_FOOTER_H - 1 }}>
            <button
              onClick={() => setStatsExpanded(v => !v)}
              title={statsExpanded ? 'Hide the counter stats panel' : 'Show counter stats, filter and copy'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                flex: '1 1 0', minWidth: 0,
                padding: '0 10px', fontSize: 12, fontWeight: 700,
                color: '#3730a3',
                background: statsExpanded ? '#c7d2fe' : '#e0e7ff',
                border: 'none', cursor: 'pointer',
                
              }}
            >
              📊 Stats {statsExpanded ? '▲' : '▼'}
            </button>
            {/* Copy Stats lives here, not inside the panel: it is one of the most-used
                actions, so it is one tap away whether the panel is open or shut, always in
                the same spot. Filter stays with the stats text it changes; when a filter is
                on, this says so, so a filtered count is never copied by surprise. */}
            <button
              onClick={handleCopyStats}
              title={statsFilterOn ? 'Copy the stats (a filter is on — only what it counts)' : 'Copy the stats to paste elsewhere'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                flex: '1 1 0', minWidth: 0, padding: '0 8px', fontSize: 12, fontWeight: 700,
                color: statsCopied ? 'white' : '#3730a3',
                background: statsCopied ? '#22c55e' : '#e0e7ff',
                border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                borderLeft: '1px solid #c7d2fe',
              }}
            >
              {statsCopied ? '✓ Copied' : statsFilterOn ? '📋 Copy (filtered)' : '📋 Copy'}
            </button>
            {mergeAddOfficers && (
              <button
                onClick={() => openDrawer('sos')}
                title="Add SOS/OT officers"
                style={{
                  flex: '1.2 1 0', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                  fontSize: 12, fontWeight: 700, border: 'none', whiteSpace: 'nowrap',
                  // Exact same solid fill + white text as the Officers-tab version
                  // below (not an outline) — same button, same look, on both tabs.
                  // Ctr Stats being wider is what marks it as this row's priority
                  // action, not a difference in the two buttons' own styling.
                  background: '#667eea', color: 'white', cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 15, lineHeight: 1, fontWeight: 400 }}>+</span>
                Add Officers
              </button>
            )}
          </div>
        </div>
        );
      })()}

      {showInbox && (
        <InboxModal
          currentVersionMessages={currentVersionMessages}
          readMsgKeys={readMsgKeys}
          setReadMsgKeys={setReadMsgKeys}
          expandedMsgKeys={expandedMsgKeys}
          setExpandedMsgKeys={setExpandedMsgKeys}
          onClose={() => setShowInbox(false)}
        />
      )}

      {/* Compare versions modal */}
      {showCompare && state && (
        <CompareVersionsModal
          history={state.history}
          officerMetadata={state.officer_metadata}
          shiftType={state.shift_type}
          operationMode={state.operation_mode}
          onClose={() => setShowCompare(false)}
          onDiffTraces={setCompareTraces}
        />
      )}

      {/* ⋯ menu portal — rendered on document.body to escape overflow:hidden ancestors */}
      {showMenu && menuBtnRef.current && createPortal(
        (() => {
          const rect = menuBtnRef.current!.getBoundingClientRect();
          return (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 210 }} onClick={() => setShowMenu(false)} />
              <div style={{ position: 'fixed', top: rect.bottom + 4, right: window.innerWidth - rect.right, background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.13)', zIndex: 211, minWidth: 190, overflow: 'hidden' }}>
                <button onClick={() => { setShowMenu(false); handleExport(); }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#1e293b', textAlign: 'left' as const }}>⬇ Download PDF</button>
                <div style={{ height: 1, background: '#f1f5f9', margin: '0 8px' }} />
                <button onClick={() => { setShowMenu(false); if (state) downloadEditLog(state.history, _sessionId); }} title="Time, kind and description of each roster edit — for usage analysis" style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#1e293b', textAlign: 'left' as const }}>⬇ Export edit log (CSV)</button>
                <div style={{ height: 1, background: '#f1f5f9', margin: '0 8px' }} />
                <button onClick={() => { setShowMenu(false); openWizard(true); }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#dc2626', textAlign: 'left' as const }}>↺ Regenerate roster</button>
              </div>
            </>
          );
        })(),
        document.body,
      )}

      {/* Full-name reveal on presence-bubble click — dismisses on next click anywhere */}
      {presenceNameHint && createPortal(
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 9500 }} onClick={() => setPresenceNameHint(null)} />
          <div
            style={{
              position: 'fixed', top: presenceNameHint.y + 12, left: presenceNameHint.x + 12,
              background: 'rgba(15,23,42,0.92)', color: 'white', padding: '5px 10px',
              borderRadius: 6, fontSize: 12, fontWeight: 600, pointerEvents: 'none',
              zIndex: 9501, whiteSpace: 'nowrap',
            }}
          >
            {presenceNameHint.userName} is editing this
          </div>
        </>,
        document.body,
      )}
    </div>
    </NoReliefProvider>
  );
}
